// UI controller for the 3D letters generator. Wires the form, the model pipeline, the viewer,
// the estimates and the downloads. All user text is treated as data: it only reaches the DOM through
// .value / .textContent and URLs through encodeURIComponent; the plan SVG is built from numbers only.
import { CUSTOM_FONT_ID, DEFAULT_FONT_ID, FONTS, FONT_BY_ID, loadFontBuffer } from './wr-l3d-fonts.js?v=aaf4a0ca9e19';
import { MATERIALS, summarize } from './wr-l3d-estimate.js?v=aaf4a0ca9e19';
import { buildReadme, orientForPrint, toAssemblySvg, toBinaryStl, toDxf, toSheetSvg, toSvg } from './wr-l3d-export.js?v=aaf4a0ca9e19';
import {
  cutCost, normalizeCuts, planSplit, splitModelToBed,
} from './wr-l3d-split.js?v=aaf4a0ca9e19';
import { EXAMPLES, applyExample } from './wr-l3d-examples.js?v=aaf4a0ca9e19';
import { KIT_FILES, buildClearanceKit, buildKitReadme, kitSupported } from './wr-l3d-kit.js?v=aaf4a0ca9e19';
import { layersToTriangles } from './wr-l3d-mesh.js?v=aaf4a0ca9e19';
import { numberFormat } from './wr-l3d-locale.js?v=aaf4a0ca9e19';
import { buildModel, buildModelFromPieces } from './wr-l3d-model.js?v=aaf4a0ca9e19';
import { nestPieces, normalizeSheet, sheetOutline } from './wr-l3d-nest.js?v=aaf4a0ca9e19';
import { createCosts } from './wr-l3d-ui-costs.js?v=aaf4a0ca9e19';
import { createLanguage } from './wr-l3d-ui-i18n.js?v=aaf4a0ca9e19';
import { createLibrary } from './wr-l3d-ui-library.js?v=aaf4a0ca9e19';
import { createTour } from './wr-l3d-ui-tour.js?v=aaf4a0ca9e19';
import { PRINTER_GROUPS, PRINTERS, printerForBed } from './wr-l3d-printers.js?v=aaf4a0ca9e19';
import { parseProject, serializeProject } from './wr-l3d-project.js?v=aaf4a0ca9e19';
import {
  describeDrawingError, detectKind, drawingToPaths, drawingToPieces, isDrawingError, nameWithoutExtension,
} from './wr-l3d-drawing.js?v=aaf4a0ca9e19';
import { buildNeonModel } from './wr-l3d-neon.js?v=aaf4a0ca9e19';
import { DEFAULT_STATE, decodeState, encodeState } from './wr-l3d-share.js?v=aaf4a0ca9e19';
import {
  STYLES, isParamActive, manualHoleFits, manualHoleZone, normalizeParams,
} from './wr-l3d-styles.js?v=aaf4a0ca9e19';
import {
  HOLE_LIMITS, addHole, holeIndexAt, removeHole,
} from './wr-l3d-holes.js?v=aaf4a0ca9e19';
import { pointInRegion } from './wr-l3d-geom.js?v=aaf4a0ca9e19';
import {
  OPTIMIZE_LIMITS, normalizeOptimize, optimizePieces,
} from './wr-l3d-optimize.js?v=aaf4a0ca9e19';
import {
  countNodesIn, curvesToLaidOut, deleteNode, fromPieces, insertNode, moveNode, nearestEdge, nearestNode, newHistory, normalizeCurves,
  pushHistory, redoHistory, undoHistory,
} from './wr-l3d-nodes.js?v=aaf4a0ca9e19';
import { standoffPlates } from './wr-l3d-standoffs.js?v=aaf4a0ca9e19';
import { parseFont, textToPieces } from './wr-l3d-text.js?v=aaf4a0ca9e19';
import { createZip } from './wr-l3d-zip.js?v=aaf4a0ca9e19';

const root = document.querySelector('[data-wr-l3d]');
if (root) init(root);

function init(host) {
  const $ = (selector) => host.querySelector(selector);
  const $$ = (selector) => [...host.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const nf1 = numberFormat({ maximumFractionDigits: 1 });
  const nf0 = numberFormat({ maximumFractionDigits: 0 });

  const els = {
    text: $('[data-l3d-text]'), textHint: $('[data-l3d-text-hint]'),
    font: $('[data-l3d-font]'), fontHint: $('[data-l3d-font-hint]'),
    fontUpload: $('[data-l3d-font-upload]'), fontFile: $('[data-l3d-font-file]'),
    height: $('[data-l3d-height]'), heightRange: $('[data-l3d-height-range]'),
    spacing: $('[data-l3d-spacing]'), spacingRange: $('[data-l3d-spacing-range]'),
    sourceButtons: $$('[data-l3d-source]'), onlyText: $$('[data-l3d-only="text"]'), onlySvg: $$('[data-l3d-only="svg"]'),
    onlyCurves: $$('[data-l3d-only="curves"]'), notInCurves: $$('[data-l3d-not-in="curves"]'), onlyNeon: $$('[data-l3d-only="neon"]'),
    neonPick: $('[data-l3d-neon-pick]'), neonFile: $('[data-l3d-neon-file]'), neonName: $('[data-l3d-neon-name]'), neonDrop: $('[data-l3d-neon-drop]'),
    nodeBox: $('[data-l3d-node-box]'), ledLabel: $('[data-l3d-led-label]'), optBox: $('[data-l3d-opt-box]'),
    nodeEnter: $('[data-l3d-node-enter]'), nodeUndo: $('[data-l3d-node-undo]'), nodeRedo: $('[data-l3d-node-redo]'), nodeAdd: $('[data-l3d-node-add]'),
    nodeDelete: $('[data-l3d-node-delete]'), nodeStats: $('[data-l3d-node-stats]'), nodeSimplify: $('[data-l3d-node-simplify]'), nodeRestart: $('[data-l3d-node-restart]'),
    svgPick: $('[data-l3d-svg-pick]'), svgFile: $('[data-l3d-svg-file]'), svgName: $('[data-l3d-svg-name]'), drop: $('[data-l3d-drop]'),
    split: $('[data-l3d-split]'), splitMargin: $('[data-l3d-split-margin]'), splitJoint: $('[data-l3d-split-joint]'), splitOptions: $('[data-l3d-split-options]'),
    splitStrategy: $('[data-l3d-split-strategy]'), cutsBox: $('[data-l3d-cuts-box]'), cutList: $('[data-l3d-cut-list]'),
    cutsResetRow: $('[data-l3d-cuts-reset-row]'), cutsReset: $('[data-l3d-cuts-reset]'),
    whiteHole: $('[data-l3d-white-hole]'), heightLabel: $('[data-l3d-height-label]'), heightHint: $('[data-l3d-height-hint]'),
    multiline: $('[data-l3d-multiline]'), leading: $('[data-l3d-leading]'), leadingRange: $('[data-l3d-leading-range]'), alignButtons: $$('[data-l3d-align]'),
    styles: $$('input[name="l3d-style"]'),
    params: $('[data-l3d-params]'), variants: $('[data-l3d-variants]'), variantsBox: $('[data-l3d-variants-box]'), resetParams: $('[data-l3d-reset-params]'),
    printer: $('[data-l3d-printer]'), bed: $$('[data-l3d-bed]'), material: $('[data-l3d-material]'), ledWatts: $('[data-l3d-led-watts]'), ledRows: $('[data-l3d-led-rows]'),
    canvas: $('[data-l3d-canvas]'), plan: $('[data-l3d-plan]'), glow: $('[data-l3d-glow]'),
    tabs: $$('[data-l3d-tab]'), lights: $('[data-l3d-lights]'), swatches: $('[data-l3d-swatches]'),
    swatchButtons: $$('[data-l3d-led-color]'), explode: $('[data-l3d-explode]'),
    parts: $('[data-l3d-parts]'), readout: $('[data-l3d-readout]'), status: $('[data-l3d-status]'),
    resetView: $('[data-l3d-reset-view]'), snapshot: $('[data-l3d-snapshot]'),
    togglePanel: $('[data-l3d-toggle-panel]'), toggleResults: $('[data-l3d-toggle-results]'), fullscreen: $('[data-l3d-fullscreen]'),
    notes: $('[data-l3d-notes]'), toast: $('[data-l3d-toast]'),
    zips: $$('[data-l3d-zip]'), stl: $('[data-l3d-stl]'), dxf: $('[data-l3d-dxf]'), svg: $('[data-l3d-svg]'),
    dxfBack: $('[data-l3d-dxf-back]'), svgBack: $('[data-l3d-svg-back]'),
    quote: $('[data-l3d-quote]'), wa: $('[data-l3d-wa]'), copy: $('[data-l3d-copy]'),
    examples: $('[data-l3d-examples]'), kit: $('[data-l3d-kit]'), sheetInputs: $$('[data-l3d-sheet]'), sheetsZip: $('[data-l3d-sheets]'),
    optOn: $('[data-l3d-opt-on]'), optTol: $('[data-l3d-opt-tol]'), optSpeck: $('[data-l3d-opt-speck]'), optCompare: $('[data-l3d-opt-compare]'),
    optCompareRow: $('[data-l3d-opt-compare-row]'), optFields: $('[data-l3d-opt-fields]'), optStats: $('[data-l3d-opt-stats]'),
    holeBox: $('[data-l3d-holes-box]'), holeD: $('[data-l3d-hole-d]'), holePresets: $$('[data-l3d-hole-preset]'), holeMode: $('[data-l3d-hole-mode]'),
    holeX: $('[data-l3d-hole-x]'), holeY: $('[data-l3d-hole-y]'), holeAdd: $('[data-l3d-hole-add]'), holeList: $('[data-l3d-hole-list]'),
    holeEmpty: $('[data-l3d-hole-empty]'), holeClear: $('[data-l3d-hole-clear]'), holeClearRow: $('[data-l3d-hole-clear-row]'),
    projectSave: $('[data-l3d-project-save]'), projectOpen: $('[data-l3d-project-open]'), projectFile: $('[data-l3d-project-file]'),
  };
  const fact = (name) => $(`[data-fact="${name}"]`);

  // ---- state ------------------------------------------------------------------
  const ui = { view: '3d', lit: true, ledColor: '#ffe9b8', explode: 0, ledWatts: 7.2, ledRows: 1, holeMode: false, compare: false, addNode: false, curvesOrigin: 'text', styleBeforeNeon: null, paramsBeforeNeon: null, holeDiameter: HOLE_LIMITS.default, hidden: new Set(), notice: '', fullscreen: false };
  const HEAVY_NODES = 8000; // above this many nodes a drawing is worth lightening with the curve optimizer
  const problems = []; // short log of what went wrong in this session, included in the diagnostics
  const state = loadState();
  // A tool page opens with its own tool, unless somebody sent a link: that one opens its own design.
  const start = host.dataset.start || 'text';
  if (!/^#d=/.test(location.hash)) {
    if (start === 'logo') state.source = 'svg';
    else if (start === 'neon') state.source = 'neon';
  }
  const fonts = new Map(); // id -> { id, label, font, family }
  let viewer = null;
  let model = null;
  let summary = null;
  let buildToken = 0;
  let buildTimer = 0;
  let persistTimer = 0;
  let framedSize = 0;
  let firstRender = true;

  function loadState() {
    const hashValue = (location.hash.match(/^#d=([A-Za-z0-9_-]+)$/) || [])[1];
    const fromHash = decodeState(hashValue);
    let stored = null;
    let last = null;
    try { last = localStorage.getItem('wr-l3d:last'); stored = decodeState(last); } catch { /* storage may be blocked */ }
    // The link in the address bar is this browser's own last design (we write it ourselves) or one somebody sent: only
    // the first goes with the logo and the curves kept in this browser.
    const ownSession = !hashValue || hashValue === last;
    const base = fromHash || stored || DEFAULT_STATE;
    // Not part of the shareable link: a logo is too big to travel in a URL.
    const local = { ...base, params: { ...base.params }, bed: { ...base.bed }, split: { ...base.split }, sheet: { ...base.sheet }, source: 'text', svg: null, whiteAsHole: true, curves: null, neon: null };
    try {
      const saved = JSON.parse(localStorage.getItem('wr-l3d:svg') || 'null');
      if (ownSession && saved && typeof saved.text === 'string' && localStorage.getItem('wr-l3d:source') === 'svg') {
        local.source = 'svg';
        local.svg = { name: String(saved.name || 'logo.svg').slice(0, 120), text: saved.text, kind: saved.kind === 'dxf' || saved.kind === 'svg' ? saved.kind : (detectKind(saved.name, saved.text) ?? 'svg') };
        local.whiteAsHole = saved.whiteAsHole !== false;
      }
    } catch { /* storage may be blocked or hold something else */ }
    try { // a neon drawing is too big for a link, like a logo: restored when the last visit ended in it
      const saved = JSON.parse(localStorage.getItem('wr-l3d:neon') || 'null');
      if (ownSession && saved && typeof saved.text === 'string' && localStorage.getItem('wr-l3d:source') === 'neon') {
        local.source = 'neon';
        local.neon = { name: String(saved.name || 'neon.svg').slice(0, 120), text: saved.text, kind: saved.kind === 'dxf' ? 'dxf' : 'svg' };
      }
    } catch { /* storage may be blocked or hold something else */ }
    try { // curves edited with the node editor: restored when the last visit ended in them (a link cannot carry them)
      const curves = ownSession && localStorage.getItem('wr-l3d:source') === 'curves' ? normalizeCurves(JSON.parse(localStorage.getItem('wr-l3d:curves') || 'null')) : null;
      if (curves) { local.source = 'curves'; local.curves = curves; }
    } catch { /* storage may be blocked or hold something else */ }
    // the neon channel only goes with a neon drawing: a link that carries it without the drawing opens with the usual style
    if (local.source !== 'neon' && local.styleId === 'neon') { local.styleId = DEFAULT_STATE.styleId; local.params = {}; }
    return local;
  }

  // ---- small helpers ------------------------------------------------------------
  function track(name, params = {}) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name, ...params });
      if (window.gtag) window.gtag('event', name, params);
    } catch { /* analytics must never break the tool */ }
    if (name.startsWith('l3d_download_')) showReviewAsk();
  }

  /** After a successful download (the moment the visitor got what they came for) we ask, without conditions, for a Google review. */
  function showReviewAsk() {
    const box = host.querySelector('[data-l3d-review]');
    if (!box) return;
    let skipped = false;
    try { skipped = localStorage.getItem('wr-l3d:review-done') === '1'; } catch { /* storage may be blocked */ }
    if (skipped) return;
    if (box.hidden) {
      box.hidden = false;
      track('l3d_review_ask');
      const link = box.querySelector('[data-l3d-review-link]');
      const skip = box.querySelector('[data-l3d-review-skip]');
      const done = () => { box.hidden = true; try { localStorage.setItem('wr-l3d:review-done', '1'); } catch { /* storage may be blocked */ } };
      link.addEventListener('click', () => { track('l3d_review_click'); done(); });
      skip.addEventListener('click', done);
    }
  }

  function setStatus(message) {
    els.status.hidden = !message;
    els.status.textContent = message || '';
  }

  /** Writes sentences as separate text nodes (a space before each but the first), so each one can be translated alone. */
  function setSentences(element, sentences) {
    const parts = sentences.filter(Boolean);
    element.replaceChildren(...parts.map((part, index) => document.createTextNode(index === 0 ? part : ` ${part}`)));
  }

  let toastTimer = 0;
  /** `message` is a sentence, or a list of sentences. */
  function toast(message) {
    setSentences(els.toast, Array.isArray(message) ? message : [message]);
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { els.toast.textContent = ''; }, 3000);
  }

  /** A multi-line text as one line for links, messages and file headers: WAR / RECORD. */
  const oneLine = (text) => text.trim().split(/\s*(?:\r\n|\r|\n)+\s*/).join(' / ');

  const slug = (text) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24) || 'diseno';

  /** The text file that explains a ZIP: LEEME in Spanish, README in the other languages. */
  const readmeName = () => (language.lang === 'es' ? 'LEEME.txt' : 'README.txt');

  function download(name, bytes, mime) {
    const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  }

  // ---- fonts ----------------------------------------------------------------------
  function applyInputFont() {
    const entry = fonts.get(state.fontId);
    // The theme forces DM Sans on every input with !important; an inline !important is the only thing that beats it.
    if (entry?.family) els.text.style.setProperty('font-family', `"${entry.family}", var(--l3d-font)`, 'important');
    else els.text.style.removeProperty('font-family');
  }

  /** Writes the design into the URL hash. Built from location.href so a <base> tag cannot redirect it off-origin. */
  function writeHash(encoded) {
    try {
      const url = new URL(location.href);
      url.hash = `d=${encoded}`;
      history.replaceState(null, '', url);
    } catch { /* sandboxed frames */ }
  }

  /** Keeps the sticky blocks clear of the store's own fixed header, which may hide on scroll. */
  function trackStickyOffset() {
    let queued = false;
    const update = () => {
      queued = false;
      let offset = 0;
      let node = document.elementFromPoint(window.innerWidth / 2, 2);
      const tallest = Math.min(220, window.innerHeight * 0.4); // a header, not a full-screen overlay
      while (node && node !== document.body && !host.contains(node)) {
        const { position } = getComputedStyle(node);
        if (position === 'fixed' || position === 'sticky') {
          const box = node.getBoundingClientRect();
          if (box.height <= tallest) offset = Math.max(offset, Math.ceil(box.bottom));
        }
        node = node.parentElement;
      }
      host.style.setProperty('--l3d-sticky-top', `${Math.min(offset, 160)}px`);
    };
    const schedule = () => {
      if (!queued) { queued = true; requestAnimationFrame(update); }
    };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  function registerCssFont(entry, buffer) {
    // Lets the text field itself show the chosen face.
    try {
      const face = new FontFace(`wrl3d-${entry.id}`, buffer.slice(0));
      face.load().then((loaded) => {
        document.fonts.add(loaded);
        entry.family = `wrl3d-${entry.id}`;
        if (state.fontId === entry.id) applyInputFont();
      }).catch(() => {});
    } catch { /* the preview in the field is a nicety */ }
  }

  /** Parses a font file, turning the parser's cryptic errors into ones the UI can explain. */
  function readFont(buffer) {
    const signature = String.fromCharCode(...new Uint8Array(buffer.slice(0, 4)));
    if (signature === 'wOF2') throw new Error('ERR_FONT_WOFF2');
    try {
      return parseFont(buffer);
    } catch (error) {
      throw new Error(`ERR_FONT_PARSE:${error?.message || error}`);
    }
  }

  async function ensureFont(fontId) {
    if (fonts.has(fontId)) return fonts.get(fontId);
    const meta = FONT_BY_ID.get(fontId);
    if (!meta) throw new Error('ERR_FONT_UNKNOWN');
    let buffer;
    try {
      buffer = await loadFontBuffer(fontId);
    } catch (error) {
      throw new Error(`ERR_FONT_LOAD:${fontId}:${error?.message || error}`);
    }
    const entry = { id: fontId, label: meta.label, font: readFont(buffer), family: null };
    registerCssFont(entry, buffer);
    fonts.set(fontId, entry);
    return entry;
  }

  function fillFontSelect() {
    els.font.replaceChildren(...FONTS.map((f) => new Option(f.label, f.id)));
    if (fonts.has(CUSTOM_FONT_ID)) els.font.add(new Option(`Mi tipografía: ${fonts.get(CUSTOM_FONT_ID).label}`, CUSTOM_FONT_ID));
    if (!fonts.has(state.fontId) && !FONT_BY_ID.has(state.fontId)) state.fontId = DEFAULT_FONT_ID;
    els.font.value = state.fontId;
    const meta = FONT_BY_ID.get(state.fontId);
    els.fontHint.hidden = !meta?.thin;
    els.fontHint.textContent = meta?.thin ? 'Esta tipografía tiene trazos finos: con un alto chico las letras pueden quedar sin acrílico. Probá con un alto mayor.' : '';
    els.fontHint.classList.add('wr-l3d__hint--warn');
  }

  // ---- form <-> state -----------------------------------------------------------------
  const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
  const toNumber = (input, fallback) => { const n = Number.parseFloat(String(input).replace(',', '.')); return Number.isFinite(n) ? n : fallback; };

  function refreshTextControls() {
    els.textHint.textContent = `${Array.from(state.text).length} de 400 caracteres. Enter agrega otra línea.`;
    els.multiline.hidden = state.source === 'svg' || !/[\r\n]/.test(state.text);
    els.text.style.height = 'auto';
    els.text.style.height = `${Math.min(190, Math.max(54, els.text.scrollHeight + 2))}px`;
  }

  /** Text or logo: shows the controls that belong to the chosen source and relabels the shared ones. */
  /** The neon channel goes with a neon drawing and only with it: switching in or out of it swaps the style (and its settings). */
  function reconcileStyle() {
    const neonMode = state.source === 'neon';
    if (neonMode && state.styleId !== 'neon') {
      ui.styleBeforeNeon = state.styleId;
      ui.paramsBeforeNeon = state.params;
      state.styleId = 'neon';
      state.params = {};
    } else if (!neonMode && state.styleId === 'neon') {
      state.styleId = ui.styleBeforeNeon && ui.styleBeforeNeon !== 'neon' ? ui.styleBeforeNeon : DEFAULT_STATE.styleId;
      state.params = ui.styleBeforeNeon ? ui.paramsBeforeNeon ?? {} : {};
    } else {
      return;
    }
    for (const radio of els.styles) {
      radio.checked = radio.value === state.styleId;
      radio.closest('label').hidden = neonMode ? radio.value !== 'neon' : radio.value === 'neon';
    }
    renderParamFields();
  }

  function applySource() {
    const svgMode = state.source === 'svg';
    const curvesMode = state.source === 'curves';
    const neonMode = state.source === 'neon';
    reconcileStyle();
    for (const radio of els.styles) radio.closest('label').hidden = neonMode ? radio.value !== 'neon' : radio.value === 'neon';
    for (const element of els.onlyText) element.hidden = svgMode || curvesMode || neonMode;
    for (const element of els.onlySvg) element.hidden = !svgMode;
    for (const element of els.onlyCurves) element.hidden = !curvesMode;
    for (const element of els.onlyNeon) element.hidden = !neonMode;
    for (const element of els.notInCurves) element.hidden = curvesMode;
    els.nodeBox.hidden = curvesMode || neonMode; // the node editor works on shapes, not on a neon channel
    els.optBox.hidden = curvesMode || neonMode; // and so does the optimizer
    els.neonName.textContent = state.neon ? `${state.neon.name} (${Math.max(1, Math.round(state.neon.text.length / 1024))} KB)` : 'Todavía no elegiste ninguno';
    els.neonName.classList.toggle('is-set', Boolean(state.neon));
    els.nodeEnter.textContent = state.curves ? 'Volver a editar los nodos' : 'Editar los nodos de este diseño';
    els.multiline.hidden = svgMode || curvesMode || !/[\r\n]/.test(state.text);
    for (const button of els.sourceButtons) button.setAttribute('aria-pressed', String(button.dataset.l3dSource === state.source));
    els.heightLabel.textContent = neonMode ? 'Alto del trazado' : (svgMode ? 'Alto del logo' : 'Alto del texto');
    els.heightHint.textContent = neonMode
      ? 'Es el alto del recorrido del neón (el centro de la línea); el canal agrega un poco a cada lado.'
      : (svgMode
        ? 'Es el alto de todo el dibujo terminado; el ancho sale de sus proporciones.'
        : 'Es el alto de todo el texto terminado, de la base de las letras al punto más alto.');
    els.svgName.textContent = state.svg ? `${state.svg.name} (${Math.max(1, Math.round(state.svg.text.length / 1024))} KB)` : 'Todavía no elegiste ninguno';
    els.svgName.classList.toggle('is-set', Boolean(state.svg));
    els.whiteHole.checked = state.whiteAsHole;
    els.whiteHole.closest('label').hidden = state.svg?.kind === 'dxf'; // white shapes only mean something in an SVG
  }

  /** What the design is called in file names, links and messages. */
  const designName = () => (state.source === 'neon' ? `neón ${nameWithoutExtension(state.neon?.name || '')}`.trim() : state.source === 'curves' ? 'curvas' : state.source === 'svg' ? `logo ${nameWithoutExtension(state.svg?.name || '')}`.trim() : oneLine(state.text));

  async function loadNeonFile(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast('Ese archivo pesa más de 8 MB. Simplificá el dibujo.'); return; }
    if (!/\.(svg|dxf)$/i.test(file.name) && file.type !== 'image/svg+xml') { toast('Tiene que ser un archivo .svg o .dxf.'); return; }
    try {
      const text = await file.text();
      const kind = detectKind(file.name, text);
      if (!kind) { toast('Tiene que ser un archivo .svg o .dxf.'); return; }
      const drawing = { name: file.name.slice(0, 120), text, kind };
      drawingToPaths(drawing, { heightMm: state.heightMm }); // validate before replacing what is on screen
      state.neon = drawing;
      state.source = 'neon';
      applySource();
      scheduleBuild(0);
      track('l3d_neon_loaded', { kb: Math.round(text.length / 1024), kind });
    } catch (error) {
      toast(describeDrawingError(error, { lines: true }));
    }
  }

  async function loadSvgFile(file) {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast('Ese archivo pesa más de 8 MB. Simplificá el dibujo.'); return; }
    if (!/\.(svg|dxf)$/i.test(file.name) && file.type !== 'image/svg+xml') { toast('Tiene que ser un archivo .svg o .dxf.'); return; }
    try {
      const text = await file.text();
      const kind = detectKind(file.name, text);
      if (!kind) { toast('Tiene que ser un archivo .svg o .dxf.'); return; }
      const drawing = { name: file.name.slice(0, 120), text, kind };
      drawingToPieces(drawing, { heightMm: state.heightMm, whiteAsHole: state.whiteAsHole }); // validate before replacing what is on screen
      state.svg = drawing;
      state.source = 'svg';
      applySource();
      scheduleBuild(0);
      track('l3d_svg_loaded', { kb: Math.round(text.length / 1024), kind });
    } catch (error) {
      toast(describeDrawingError(error));
    }
  }

  function syncAlignButtons() {
    for (const button of els.alignButtons) button.setAttribute('aria-pressed', String(button.dataset.l3dAlign === state.align));
  }

  function syncForm() {
    els.text.value = state.text;
    refreshTextControls();
    applySource();
    els.leading.value = String(state.lineSpacing);
    els.leadingRange.value = String(state.lineSpacing);
    syncAlignButtons();
    els.height.value = String(state.heightMm / 10);
    els.heightRange.value = String(state.heightMm / 10);
    els.spacing.value = String(state.letterSpacingMm);
    els.spacingRange.value = String(state.letterSpacingMm);
    for (const radio of els.styles) radio.checked = radio.value === state.styleId;
    for (const input of els.bed) input.value = String(state.bed[input.dataset.l3dBed]);
    for (const input of els.sheetInputs) input.value = String(state.sheet[input.dataset.l3dSheet]);
    syncPrinterSelect();
    els.split.checked = state.split.on;
    els.splitMargin.value = String(state.split.margin);
    els.splitJoint.value = state.split.joint;
    els.splitStrategy.value = state.split.strategy;
    els.splitOptions.hidden = !state.split.on;
    els.material.value = state.material;
    els.ledWatts.value = String(ui.ledWatts);
    els.ledRows.value = String(ui.ledRows);
    fillFontSelect();
    applyInputFont();
    renderParamFields();
    renderHolePanel();
    syncOptimize();
  }

  /** The printer drop-down: "other" plus the known models by brand; it always shows the model the bed matches. */
  function fillPrinterSelect() {
    const groups = PRINTER_GROUPS.map(({ brand, printers }) => {
      const group = document.createElement('optgroup');
      group.label = brand;
      for (const printer of printers) group.append(new Option(printer.label, printer.id)); // the bed sizes fill the fields just below
      return group;
    });
    els.printer.replaceChildren(new Option('Otra impresora (medidas propias)', ''), ...groups);
  }

  function syncPrinterSelect() {
    els.printer.value = printerForBed(state.bed)?.id ?? '';
  }

  /** One setting as a labelled field; `p.when` settings are shown only while they apply (see refreshConditions). */
  function paramField(p, values) {
    const wrap = document.createElement('label');
    wrap.className = 'wr-l3d__param';
    wrap.dataset.paramField = p.id;
    const caption = document.createElement('span');
    caption.className = 'wr-l3d__label';
    caption.textContent = p.label;
    if (p.type === 'select') {
      const select = document.createElement('select');
      select.className = 'wr-l3d__input';
      select.dataset.param = p.id;
      for (const option of p.options) {
        const item = document.createElement('option');
        item.value = String(option.value);
        item.textContent = option.label;
        select.append(item);
      }
      select.value = String(values[p.id]);
      select.addEventListener('change', () => {
        state.params[p.id] = p.numeric ? Number(select.value) : select.value;
        refreshConditions();
        scheduleBuild(0);
      });
      wrap.append(caption, select);
      return wrap;
    }
    const input = document.createElement('input');
    input.className = 'wr-l3d__input';
    input.type = 'number';
    input.inputMode = 'decimal';
    input.min = String(p.min);
    input.max = String(p.max);
    input.step = String(p.step);
    input.dataset.param = p.id;
    if (p.auto) {
      input.placeholder = 'Automática';
      input.value = state.params[p.id] == null ? '' : String(state.params[p.id]);
    } else {
      input.value = String(values[p.id]);
    }
    input.addEventListener('input', () => {
      state.params[p.id] = input.value === '' ? null : toNumber(input.value, p.default);
      scheduleBuild(250);
    });
    input.addEventListener('change', () => {
      if (!p.auto) input.value = String(normalizeParams(state.styleId, state.params)[p.id]);
    });
    wrap.append(caption, input);
    return wrap;
  }

  /** Shows each setting only while it applies (the length of an oblong, the size of spacers that are switched off). */
  function refreshConditions() {
    const values = normalizeParams(state.styleId, state.params);
    const applies = new Map(STYLES[state.styleId].params.map((p) => [p.id, isParamActive(p, values)]));
    for (const field of host.querySelectorAll('[data-param-field]')) field.hidden = !applies.get(field.dataset.paramField);
    for (const heading of host.querySelectorAll('[data-param-group]')) {
      const members = STYLES[state.styleId].params.filter((p) => p.group === heading.dataset.paramGroup && !p.variant);
      heading.hidden = !members.some((p) => applies.get(p.id));
    }
    els.variantsBox.hidden = !STYLES[state.styleId].params.some((p) => p.variant && applies.get(p.id));
  }

  function renderParamFields() {
    const style = STYLES[state.styleId];
    const values = normalizeParams(state.styleId, state.params);
    els.variants.replaceChildren(...style.params.filter((p) => p.variant).map((p) => paramField(p, values)));
    const fine = [];
    let group = null;
    for (const p of style.params.filter((q) => !q.variant)) {
      if (p.group && p.group !== group) {
        const heading = document.createElement('span');
        heading.className = 'wr-l3d__param-group';
        heading.dataset.paramGroup = p.group;
        heading.textContent = p.group;
        fine.push(heading);
      }
      group = p.group ?? null;
      fine.push(paramField(p, values));
    }
    els.params.replaceChildren(...fine);
    refreshConditions();
  }

  function bindForm() {
    els.text.addEventListener('input', () => {
      state.text = els.text.value;
      refreshTextControls();
      scheduleBuild(200);
    });
    for (const button of els.sourceButtons) {
      button.addEventListener('click', () => {
        state.source = button.dataset.l3dSource;
        applySource();
        scheduleBuild(0);
      });
    }
    els.neonPick.addEventListener('click', () => els.neonFile.click());
    els.neonFile.addEventListener('change', () => { const file = els.neonFile.files?.[0]; els.neonFile.value = ''; loadNeonFile(file); });
    for (const type of ['dragenter', 'dragover']) els.neonDrop.addEventListener(type, (event) => { event.preventDefault(); els.neonDrop.classList.add('is-over'); });
    for (const type of ['dragleave', 'drop']) els.neonDrop.addEventListener(type, (event) => { event.preventDefault(); els.neonDrop.classList.remove('is-over'); });
    els.neonDrop.addEventListener('drop', (event) => loadNeonFile(event.dataTransfer?.files?.[0]));
    els.svgPick.addEventListener('click', () => els.svgFile.click());
    els.svgFile.addEventListener('change', () => { const file = els.svgFile.files?.[0]; els.svgFile.value = ''; loadSvgFile(file); });
    els.whiteHole.addEventListener('change', () => { state.whiteAsHole = els.whiteHole.checked; scheduleBuild(0); });
    for (const type of ['dragenter', 'dragover']) {
      els.drop.addEventListener(type, (event) => { event.preventDefault(); els.drop.classList.add('is-over'); });
    }
    for (const type of ['dragleave', 'drop']) {
      els.drop.addEventListener(type, (event) => { event.preventDefault(); els.drop.classList.remove('is-over'); });
    }
    els.drop.addEventListener('drop', (event) => loadSvgFile(event.dataTransfer?.files?.[0]));
    const setLeading = (value, fromRange) => {
      const v = toNumber(value, NaN);
      if (!Number.isFinite(v)) return;
      state.lineSpacing = clamp(v, 0.6, 4);
      if (fromRange) els.leading.value = String(state.lineSpacing); else els.leadingRange.value = String(state.lineSpacing);
      scheduleBuild(fromRange ? 120 : 250);
    };
    els.leading.addEventListener('input', () => setLeading(els.leading.value, false));
    els.leadingRange.addEventListener('input', () => setLeading(els.leadingRange.value, true));
    for (const button of els.alignButtons) {
      button.addEventListener('click', () => { state.align = button.dataset.l3dAlign; syncAlignButtons(); scheduleBuild(0); });
    }
    els.font.addEventListener('change', () => {
      state.fontId = els.font.value;
      fillFontSelect();
      applyInputFont();
      scheduleBuild(0);
    });
    els.fontUpload.addEventListener('click', () => els.fontFile.click());
    els.fontFile.addEventListener('change', onCustomFont);

    const pairHeight = (value) => { state.heightMm = clamp(toNumber(value, state.heightMm / 10), 1, 500) * 10; els.height.value = String(state.heightMm / 10); els.heightRange.value = String(state.heightMm / 10); scheduleBuild(120); };
    els.height.addEventListener('input', () => { const v = toNumber(els.height.value, NaN); if (Number.isFinite(v) && v >= 1) { state.heightMm = clamp(v, 1, 500) * 10; els.heightRange.value = String(v); scheduleBuild(250); } });
    els.heightRange.addEventListener('input', () => pairHeight(els.heightRange.value));
    const pairSpacing = (value) => { state.letterSpacingMm = clamp(toNumber(value, 0), -20, 100); els.spacing.value = String(state.letterSpacingMm); els.spacingRange.value = String(state.letterSpacingMm); scheduleBuild(120); };
    els.spacing.addEventListener('input', () => { const v = toNumber(els.spacing.value, NaN); if (Number.isFinite(v)) { state.letterSpacingMm = clamp(v, -20, 100); els.spacingRange.value = String(v); scheduleBuild(250); } });
    els.spacingRange.addEventListener('input', () => pairSpacing(els.spacingRange.value));

    for (const radio of els.styles) {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        const keep = new Set(STYLES[radio.value].params.map((p) => p.id));
        state.params = Object.fromEntries(Object.entries(state.params).filter(([key]) => keep.has(key)));
        state.styleId = radio.value;
        renderParamFields();
        scheduleBuild(0);
      });
    }
    els.resetParams.addEventListener('click', () => { state.params = {}; renderParamFields(); scheduleBuild(0); });

    els.printer.addEventListener('change', () => {
      const printer = PRINTERS.find((p) => p.id === els.printer.value);
      if (!printer) return; // "other": keep the bed as it is, the maker types the sizes
      state.bed = { width: printer.width, depth: printer.depth, height: printer.height };
      for (const input of els.bed) input.value = String(state.bed[input.dataset.l3dBed]);
      track('l3d_printer', { printer: printer.id });
      scheduleBuild(0); // the bed decides where pieces are cut
    });
    for (const input of els.bed) {
      input.addEventListener('input', () => {
        state.bed[input.dataset.l3dBed] = clamp(toNumber(input.value, state.bed[input.dataset.l3dBed]), 50, 2000);
        syncPrinterSelect();
        scheduleBuild(250); // the bed decides where pieces are cut
      });
    }
    for (const input of els.sheetInputs) {
      input.addEventListener('input', () => {
        const value = toNumber(input.value, NaN);
        if (!Number.isFinite(value)) return;
        state.sheet = normalizeSheet({ ...state.sheet, [input.dataset.l3dSheet]: value });
        schedulePersist();
        if (model) renderResults(); // the layout on the sheets changes; the letters do not
      });
      input.addEventListener('change', () => { input.value = String(state.sheet[input.dataset.l3dSheet]); });
    }
    els.split.addEventListener('change', () => { state.split.on = els.split.checked; els.splitOptions.hidden = !state.split.on; scheduleBuild(0); });
    els.splitMargin.addEventListener('input', () => { state.split.margin = clamp(toNumber(els.splitMargin.value, 5), 0, 30); scheduleBuild(250); });
    els.splitJoint.addEventListener('change', () => { state.split.joint = els.splitJoint.value === 'straight' ? 'straight' : 'dovetail'; scheduleBuild(0); });
    els.splitStrategy.addEventListener('change', () => {
      state.split.strategy = els.splitStrategy.value === 'equal' ? 'equal' : 'cheap';
      state.split.cuts = []; // the cuts moved by hand were relative to the old way of choosing
      scheduleBuild(0);
    });
    els.cutsReset.addEventListener('click', () => { state.split.cuts = []; scheduleBuild(0); });
    els.material.addEventListener('change', () => { state.material = els.material.value; refreshEstimates(); });
    els.ledWatts.addEventListener('change', () => { ui.ledWatts = toNumber(els.ledWatts.value, 7.2); refreshEstimates(); });
    els.ledRows.addEventListener('change', () => { ui.ledRows = clamp(Math.round(toNumber(els.ledRows.value, 1)), 1, 3); refreshEstimates(); });
  }

  async function onCustomFont() {
    const file = els.fontFile.files?.[0];
    els.fontFile.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast('Esa tipografía pesa más de 8 MB.'); return; }
    try {
      const buffer = await file.arrayBuffer();
      const entry = { id: CUSTOM_FONT_ID, label: file.name.replace(/\.[^.]+$/, ''), font: readFont(buffer), family: null };
      registerCssFont(entry, buffer);
      fonts.set(CUSTOM_FONT_ID, entry);
      state.fontId = CUSTOM_FONT_ID;
      fillFontSelect();
      applyInputFont();
      scheduleBuild(0);
      toast('Tipografía cargada. El enlace para compartir no la incluye.');
    } catch (error) {
      toast(String(error?.message).startsWith('ERR_FONT_WOFF2')
        ? 'Ese formato (WOFF2) no se puede leer. Usá un archivo .ttf, .otf o .woff.'
        : 'No pudimos leer ese archivo. Probá con un .ttf, .otf o .woff.');
    }
  }

  // ---- build pipeline --------------------------------------------------------------
  function scheduleBuild(delay) {
    clearTimeout(buildTimer);
    buildTimer = setTimeout(rebuild, delay);
  }

  function describeError(error) {
    const code = String(error?.message || '');
    if (code.startsWith('ERR_TEXT_EMPTY')) return 'Escribí un texto para empezar.';
    if (code.startsWith('ERR_TEXT_NO_PATHS')) return 'Esta tipografía no tiene ninguno de esos caracteres. Probá con otra.';
    if (code.startsWith('ERR_TEXT_HEIGHT')) return 'Poné un alto mayor a cero.';
    if (code.startsWith('ERR_TEXT_LINES')) return 'Hay demasiadas líneas: el máximo es 12.';
    if (code.startsWith('ERR_FONT')) return 'No pudimos cargar la tipografía. Revisá tu conexión y recargá la página.';
    return 'No pudimos generar las letras. Probá con otro texto o tipografía.';
  }

  /** Resolves once the browser has had a chance to paint (never later than ~100 ms, even in a hidden tab). */
  const nextPaint = () => new Promise((resolve) => {
    const timer = setTimeout(resolve, 100);
    requestAnimationFrame(() => setTimeout(() => { clearTimeout(timer); resolve(); }, 0));
  });

  async function buildTextModel(token) {
    let entry;
    try {
      if (!fonts.has(state.fontId)) setStatus('Cargando la tipografía…');
      entry = await ensureFont(state.fontId);
    } catch (error) {
      if (state.fontId === DEFAULT_FONT_ID) throw error; // nothing to fall back to: show the real reason
      console.warn('wr-l3d: font failed, using the default one:', error);
      state.fontId = DEFAULT_FONT_ID;
      fillFontSelect();
      toast('No pudimos cargar esa tipografía; usamos la de siempre.');
      entry = await ensureFont(DEFAULT_FONT_ID);
    }
    if (token !== buildToken) return null;
    // a perforated front or a very long text takes a few seconds and freezes the page: say so before it starts
    if (state.styleId === 'perforado' || Array.from(state.text).length > 60) {
      setStatus('Generando las letras…');
      await nextPaint();
      if (token !== buildToken) return null;
    }
    return buildModel(entry.font, {
      text: state.text,
      heightMm: state.heightMm,
      letterSpacingMm: state.letterSpacingMm,
      lineSpacing: state.lineSpacing,
      align: state.align,
      styleId: state.styleId,
      params: state.params,
      holes: state.holes,
      optimize: state.optimize,
    });
  }

  async function buildSvgModel(token) {
    setStatus('Procesando el logo…');
    await new Promise((resolve) => setTimeout(resolve, 0)); // let the message paint before the heavy work
    if (token !== buildToken) return null;
    const laidOut = drawingToPieces(state.svg, { heightMm: state.heightMm, whiteAsHole: state.whiteAsHole });
    return buildModelFromPieces(laidOut, {
      styleId: state.styleId, params: state.params, holes: state.holes, optimize: state.optimize,
    });
  }

  async function buildCurvesModel(token) {
    setStatus('Procesando las curvas…');
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (token !== buildToken) return null;
    return buildModelFromPieces(curvesToLaidOut(state.curves), { styleId: state.styleId, params: state.params, holes: state.holes });
  }

  async function buildNeonFromState(token) {
    setStatus('Procesando el trazado…');
    await new Promise((resolve) => setTimeout(resolve, 0));
    if (token !== buildToken) return null;
    const lines = drawingToPaths(state.neon, { heightMm: state.heightMm });
    const built = buildNeonModel(lines.paths, { params: state.params, holes: state.holes });
    if (!built) throw Object.assign(new Error('ERR_SVG_EMPTY'), { warnings: lines.warnings });
    built.notes = [...built.notes, ...lines.warnings];
    return built;
  }

  async function rebuild() {
    const token = ++buildToken;
    const svgMode = state.source === 'svg';
    const curvesMode = state.source === 'curves';
    const neonMode = state.source === 'neon';
    if (neonMode ? !state.neon : (curvesMode ? !state.curves : (svgMode ? !state.svg : !state.text.trim()))) {
      model = null;
      summary = null;
      setStatus(neonMode ? 'Elegí un dibujo de líneas (SVG o DXF) para empezar.' : (curvesMode ? 'Todavía no hay curvas: pasá un texto o un logo a curvas.' : (svgMode ? 'Elegí un archivo SVG para empezar.' : 'Escribí un texto para empezar.')));
      renderResults();
      return;
    }
    try {
      const built = neonMode ? await buildNeonFromState(token) : (curvesMode ? await buildCurvesModel(token) : (svgMode ? await buildSvgModel(token) : await buildTextModel(token)));
      if (!built || token !== buildToken) return;
      const needsSplit = state.split.on && built.pieces.some((piece) => !planSplit([piece.silhouette], state.bed, { margin: state.split.margin }).fits);
      if (needsSplit) {
        setStatus('Partiendo las letras que no entran en tu cama…');
        await nextPaint();
        if (token !== buildToken) return;
      }
      model = state.split.on
        ? splitModelToBed(built, {
          bed: state.bed, margin: state.split.margin, joint: state.split.joint, cuts: state.split.cuts, strategy: state.split.strategy,
        })
        : built;
      summary = summarize(model, { material: state.material, bed: state.bed, ledWattsPerMeter: ui.ledWatts, ledRows: ui.ledRows });
      await viewerReady;
      if (token !== buildToken) return;
      const isFirst = firstRender;
      renderModel();
      setStatus('');
      schedulePersist();
      if (isFirst) {
        track('l3d_generate', { style: state.styleId });
        verifyThreeD();
      }
    } catch (error) {
      if (token !== buildToken) return;
      model = null;
      summary = null;
      const known = /^ERR_(TEXT|SVG|DXF)/.test(String(error?.message));
      if (!known) console.error(error);
      setStatus(isDrawingError(error) ? describeDrawingError(error, { lines: neonMode }) : describeError(error));
      renderResults();
    }
  }

  function refreshEstimates() {
    if (!model) return;
    summary = summarize(model, { material: state.material, bed: state.bed, ledWattsPerMeter: ui.ledWatts, ledRows: ui.ledRows });
    renderResults();
  }

  // ---- rendering ---------------------------------------------------------------------
  const PART_COLORS = { canal: '#2fb7c4', tapa: '#009fb0', acrilico: '#bff2f3', base: '#d9dde1', fondo: '#efefe9', cuerpo: '#f1f2f4', separadores: '#c3c9d0' };

  function renderModel() {
    const size = Math.max(model.widthMm, model.heightMm);
    const ratio = framedSize ? size / framedSize : 0;
    const reframe = !framedSize || ratio < 0.85 || ratio > 1.18;
    if (reframe) framedSize = size;
    if (viewer) {
      viewer.setModel(model, { resetCamera: reframe });
      for (const id of ui.hidden) viewer.setPartVisible(id, false);
      if (firstRender) viewer.setLit(ui.lit, { color: ui.ledColor, immediate: reducedMotion });
    }
    firstRender = false;
    renderParts();
    renderPlan();
    renderCutList();
    renderHolePanel();
    renderOptimizeStats();
    renderResults();
  }

  function renderParts() {
    const seen = new Set();
    const parts = model.pieces.flatMap((piece) => piece.parts).filter((part) => !seen.has(part.id) && seen.add(part.id));
    els.parts.replaceChildren(...parts.map((part) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wr-l3d__chip wr-l3d__part';
      button.setAttribute('aria-pressed', String(!ui.hidden.has(part.id)));
      button.style.color = PART_COLORS[part.id] ?? '#fff';
      const dot = document.createElement('span');
      dot.className = 'wr-l3d__dot';
      dot.setAttribute('aria-hidden', 'true');
      const name = document.createElement('span');
      name.style.color = '#edeff0';
      name.textContent = part.label.replace(/ \(.*\)$/, '');
      button.append(dot, name);
      button.addEventListener('click', () => {
        const visible = ui.hidden.has(part.id);
        if (visible) ui.hidden.delete(part.id); else ui.hidden.add(part.id);
        button.setAttribute('aria-pressed', String(visible));
        viewer?.setPartVisible(part.id, visible);
      });
      return button;
    }));
  }

  function ringPath(ring) {
    return `M${ring.map(([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`).join('L')}Z`;
  }

  function renderPlan() {
    const w = model.widthMm;
    const h = model.heightMm;
    const pad = (Math.max(w, h) * 0.12) + 8;
    const width = w + (2 * pad);
    const height = h + (2 * pad);
    const target = Math.max(width, height) / 40;
    const step = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000].find((s) => s >= target) ?? 1000;
    const lines = [];
    for (let x = Math.ceil(-pad / step) * step; x <= w + pad; x += step) {
      lines.push(`<line class="${x % (step * 5) === 0 ? 'grid-major' : 'grid-minor'}" vector-effect="non-scaling-stroke" x1="${x}" y1="${-pad}" x2="${x}" y2="${h + pad}"/>`);
    }
    for (let y = Math.ceil(-pad / step) * step; y <= h + pad; y += step) {
      lines.push(`<line class="${y % (step * 5) === 0 ? 'grid-major' : 'grid-minor'}" vector-effect="non-scaling-stroke" x1="${-pad}" y1="${y}" x2="${w + pad}" y2="${y}"/>`);
    }
    const flagged = new Set(model.warnings.map((warning) => warning.pieceIndex));
    const before = ui.compare && model.optimization
      ? model.optimization.before.map((piece) => `<path class="piece-before" vector-effect="non-scaling-stroke" fill-rule="evenodd" d="${[piece.outer, ...piece.holes].map(ringPath).join('')}"/>`)
      : [];
    const pieces = inNodeMode() ? state.curves.pieces.map((piece, index) => `<path class="piece" data-piece-path="${index}" vector-effect="non-scaling-stroke" style="stroke-width:1.6px" fill-rule="evenodd" d="${pathOfPiece(piece)}"/>`) : model.pieces.map((piece) => {
      const d = [piece.silhouette.outer, ...piece.silhouette.holes].map(ringPath).join('');
      return `<path class="piece${flagged.has(piece.index) ? ' has-warning' : ''}" vector-effect="non-scaling-stroke" style="stroke-width:1.6px" fill-rule="evenodd" d="${d}"/>`;
    });
    const fontSize = clamp(Math.max(w, h) / 38, 3, 40);
    const standoffMarks = model.pieces.flatMap((piece) => (piece.parts.find((part) => part.accessory)?.spots ?? [])
      .map(([x, y]) => `<circle class="standoff" vector-effect="non-scaling-stroke" cx="${x.toFixed(2)}" cy="${y.toFixed(2)}" r="${(model.params.standoffDiameter / 2).toFixed(2)}"/>`));
    const neonLines = (model.neon?.paths ?? []).map((line) => `<path class="neon-line" vector-effect="non-scaling-stroke" d="M${line.points.map(([px, py]) => `${px.toFixed(2)} ${py.toFixed(2)}`).join('L')}${line.closed ? 'Z' : ''}"/>`);
    const holeRadius = Math.max(w, h) / 220; // a small hole must still be something you can see and tap
    const holeMarks = (model.holes ?? []).map((hole) => `<circle class="manual-hole is-${hole.status}" vector-effect="non-scaling-stroke" cx="${hole.x}" cy="${hole.y}" r="${Math.max(hole.d / 2, holeRadius).toFixed(2)}"/>`);
    const cutLines = [];
    const tileLabels = [];
    for (const piece of model.pieces) {
      const box = piece.bounds;
      for (const cut of piece.cuts ?? []) {
        const [x1, y1, x2, y2] = cut.axis === 'x' ? [cut.at, box.minY, cut.at, box.maxY] : [box.minX, cut.at, box.maxX, cut.at];
        const name = `${piece.index}-${cut.axis}-${cut.index}`;
        cutLines.push(`<line class="cut" data-cut-line="${name}" vector-effect="non-scaling-stroke" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"/>`);
        cutLines.push(`<line class="cut-hit" data-cut data-piece="${piece.index}" data-axis="${cut.axis}" data-index="${cut.index}" data-lo="${cut.lo}" data-hi="${cut.hi}" vector-effect="non-scaling-stroke" x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"/>`);
        cutLines.push(`<circle class="cut-handle" data-cut-line="${name}" vector-effect="non-scaling-stroke" cx="${x1}" cy="${y1}" r="${(fontSize * 0.28).toFixed(2)}"/>`);
        cutLines.push(`<circle class="cut-handle" data-cut-line="${name}" vector-effect="non-scaling-stroke" cx="${x2}" cy="${y2}" r="${(fontSize * 0.28).toFixed(2)}"/>`);
      }
      for (const tile of piece.tiles ?? []) {
        const cx = (Math.max(tile.x0, box.minX) + Math.min(tile.x1, box.maxX)) / 2;
        const cy = (Math.max(tile.y0, box.minY) + Math.min(tile.y1, box.maxY)) / 2;
        tileLabels.push(`<text class="tile" x="${cx}" y="${h - cy}" font-size="${fontSize * 0.85}" text-anchor="middle" dominant-baseline="middle">${String(piece.index + 1).padStart(2, '0')}${tile.letter}</text>`);
      }
    }
    const tick = fontSize * 0.45;
    const top = -pad * 0.55;
    const right = w + (pad * 0.55);
    const dims = `
      <g class="dim" vector-effect="non-scaling-stroke">
        <path vector-effect="non-scaling-stroke" d="M0 ${top}H${w}M0 ${top - tick}V${top + tick}M${w} ${top - tick}V${top + tick}"/>
        <path vector-effect="non-scaling-stroke" d="M${right} 0V${h}M${right - tick} 0H${right + tick}M${right - tick} ${h}H${right + tick}"/>
      </g>
      <text x="${w / 2}" y="${top - (fontSize * 0.45)}" font-size="${fontSize}" text-anchor="middle">${nf1.format(w)} mm</text>
      <text transform="translate(${right + (fontSize * 1.15)} ${h / 2}) rotate(-90)" font-size="${fontSize}" text-anchor="middle">${nf1.format(h)} mm</text>`;
    setPlanHome(-pad, -pad, width, height);
    els.plan.classList.toggle('is-adding', ui.addNode);
    els.plan.innerHTML = `<g transform="translate(0 ${h}) scale(1 -1)">${lines.join('')}${pieces.join('')}${neonLines.join('')}${before.join('')}${standoffMarks.join('')}${holeMarks.join('')}${cutLines.join('')}<g data-node-layer></g></g>${tileLabels.join('')}${dims}`;
    renderNodeLayer();
  }

  function mergedMessages(items, describe) {
    const groups = new Map();
    for (const item of items) {
      const key = describe(item);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item.pieceIndex + 1);
    }
    return [...groups].map(([message, pieceNumbers]) => {
      const unique = [...new Set(pieceNumbers)];
      const list = unique.length > 6 ? `${unique.slice(0, 6).join(', ')} y ${unique.length - 6} más` : unique.join(', ');
      return `${message} (letra${unique.length > 1 ? 's' : ''} ${list})`;
    });
  }

  function renderResults() {
    const has = Boolean(model && summary);
    const kitOk = kitSupported(state.styleId);
    els.kit.disabled = !kitOk;
    els.kit.title = kitOk ? '' : 'Este estilo no tiene encastres: no hay holgura para probar.';
    for (const button of [...els.zips, els.stl]) button.disabled = !has;
    const hasAcrylic = has && summary.acrylicAreaCm2 > 0;
    const hasBack = has && summary.backAreaCm2 > 0;
    for (const button of [els.dxf, els.svg]) {
      button.disabled = !hasAcrylic;
      button.title = has && !hasAcrylic ? 'Este estilo no lleva acrílico' : '';
    }
    for (const button of [els.dxfBack, els.svgBack]) button.hidden = !hasBack;
    els.sheetsZip.disabled = !(hasAcrylic || hasBack);
    els.sheetsZip.title = hasAcrylic || hasBack ? '' : 'Este estilo no tiene piezas para cortar';
    els.swatches.hidden = !ui.lit;
    if (!has) {
      for (const name of ['pieces', 'filament', 'led', 'acrylic']) fact(name).textContent = '—';
      els.readout.textContent = '';
      els.notes.replaceChildren();
      costs.render();
      updateLinks();
      return;
    }
    fact('pieces').textContent = nf0.format(summary.pieceCount);
    if (wasSplit()) {
      const tilesNote = document.createElement('small');
      tilesNote.textContent = `${nf0.format(model.splitInfo.tiles)} tramos`;
      fact('pieces').append(tilesNote);
    }
    const grams = summary.filament.grams;
    fact('filament').textContent = grams >= 1000 ? `${nf1.format(grams / 1000)} kg` : `${nf0.format(grams)} g`;
    const filamentNote = document.createElement('small');
    filamentNote.textContent = MATERIALS[state.material].label;
    fact('filament').append(filamentNote);
    els.ledLabel.textContent = summary.led?.kind === 'neon' ? 'Neón flex' : 'Tira LED';
    if (summary.led) {
      fact('led').textContent = `${nf1.format(summary.led.meters)} m`;
      const ledNote = document.createElement('small');
      ledNote.textContent = `${nf0.format(summary.led.watts)} W, fuente de ${nf0.format(Math.ceil(summary.led.supplyWatts))} W${summary.led.rows > 1 ? `, ${summary.led.rows} filas` : ''}`;
      fact('led').append(ledNote);
    } else {
      fact('led').textContent = 'No lleva';
    }
    fact('acrylic').textContent = summary.acrylicAreaCm2 > 0 ? `${nf0.format(summary.acrylicAreaCm2)} cm²` : 'No lleva';
    if (summary.backAreaCm2 > 0) {
      const backNote = document.createElement('small');
      backNote.textContent = `y ${nf0.format(summary.backAreaCm2)} cm² de PVC`;
      fact('acrylic').append(backNote);
    }
    els.readout.textContent = `${nf1.format(model.widthMm)} × ${nf1.format(model.heightMm)} × ${nf1.format(model.depthMm)} mm`;

    const notes = []; // each note is { sentences: [...], ok }
    for (const note of model.notes) notes.push({ sentences: [note.message] });
    if (model.missing.length) notes.push({ sentences: [`La tipografía no tiene estos caracteres y los omitimos: ${[...new Set(model.missing)].join(' ')}`] });
    for (const text of mergedMessages(model.warnings, (w) => w.message)) notes.push({ sentences: [text] });
    if (state.source === 'curves') notes.push({ sentences: ['El enlace para compartir no lleva las curvas editadas: para guardarlas usá «Guardar proyecto».'] });
    if (state.source === 'neon') notes.push({ sentences: ['El enlace para compartir no lleva el dibujo del neón: para guardarlo usá «Guardar proyecto».'] });
    if (!state.optimize.on && state.source !== 'curves' && model.nodes > HEAVY_NODES) {
      notes.push({ sentences: [`El dibujo tiene ${nf0.format(model.nodes)} nodos: si el archivo te queda pesado, probá «Optimizar curvas» (en Medidas y holguras).`] });
    }
    const bed = state.bed;
    const BED_PART = { tapa: ['la tapa', 'mide'], base: ['la base', 'mide'], separadores: ['los separadores', 'miden'] };
    const bedItems = summary.problems.map((p) => ({ ...p, partLabel: (BED_PART[p.partId] ?? ['la pieza', 'mide'])[0], verb: (BED_PART[p.partId] ?? ['la pieza', 'mide'])[1] }));
    for (const text of mergedMessages(bedItems, (p) => (p.reason === 'z'
      ? `${p.partLabel} ${p.verb} ${nf0.format(p.depthMm)} mm de alto y tu impresora llega a ${nf0.format(bed.height)} mm`
      : `${p.partLabel} ${p.verb} ${nf0.format(p.widthMm)} × ${nf0.format(p.heightMm)} mm y no entra en tu cama de ${nf0.format(bed.width)} × ${nf0.format(bed.depth)} mm`))) {
      notes.push({ sentences: [`${text.charAt(0).toUpperCase()}${text.slice(1)}.`, /^los separadores/.test(text) ? 'Bajá el alto del separador.' : 'Bajá el alto del texto o usá una cama más grande.'] });
    }
    for (const { group, layout } of sheetLayouts()) {
      const plain = (n) => String(Math.round(n)); // sheet sizes read 1220 x 2440, never 1.220
      const size = `${plain(layout.sheet.width)} × ${plain(layout.sheet.height)} mm`;
      const where = mergedMessages(layout.oversize.map((o) => ({ ...o, pieceIndex: o.id - 1 })),
        (o) => `El ${group.label} mide ${plain(o.width)} × ${plain(o.height)} mm y no entra en tu plancha de ${size}`);
      for (const text of where) notes.push({ sentences: [`${text}.`, 'Usá una plancha más grande o dividí la pieza en tu programa de corte.'] });
      const label = `${group.label.charAt(0).toUpperCase()}${group.label.slice(1)}`;
      if (layout.sheets.length === 1) {
        const { extent } = layout.sheets[0];
        notes.push({ sentences: [`${label}: entra en 1 plancha de ${size} y ocupa unos ${plain(extent.width)} × ${plain(extent.height)} mm.`], ok: true });
      } else if (layout.sheets.length > 1) {
        notes.push({ sentences: [`${label}: necesitás ${layout.sheets.length} planchas de ${size}.`], ok: true });
      }
    }
    if (wasSplit()) {
      const { pieces: cutPieces, tiles } = model.splitInfo;
      const joints = state.split.joint === 'dovetail' ? 'Las bases encastran con colas de milano y las paredes se pegan.' : 'Los cortes son rectos: se pegan.';
      notes.push({
        sentences: [`${cutPieces === 1 ? 'Una letra se partió' : `${cutPieces} letras se partieron`} en ${tiles} tramos (A, B, C…) para que entren en tu cama.`, joints, 'El ZIP trae el plano de armado.'],
        ok: true,
      });
    }
    if (!summary.problems.length) notes.push({ sentences: ['Todas las piezas entran en tu cama de impresión.'], ok: true });
    if (ui.notice) notes.unshift({ sentences: [ui.notice] });
    els.notes.replaceChildren(...notes.map((note) => {
      const li = document.createElement('li');
      li.className = `wr-l3d__note${note.ok ? ' wr-l3d__note--ok' : ''}`;
      setSentences(li, note.sentences);
      return li;
    }));
    costs.render();
    updateLinks();
  }

  /** Copies text to the clipboard; false when the browser does not allow it. */
  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const copied = Boolean(document.execCommand && document.execCommand('copy'));
      area.remove();
      return copied;
    }
  }

  // ---- costs and quotes (own module) -----------------------------------------------------
  const sheetCounts = () => Object.fromEntries(sheetLayouts().map(({ group, layout }) => [group.id, layout.sheets.length]));
  const costs = createCosts({
    $,
    $$,
    state,
    nf1,
    track,
    toast,
    copyText,
    toNumber,
    designName: () => designName(),
    getModel: () => model,
    getSummary: () => summary,
    sheetCounts,
    snapshot: () => (viewer ? viewer.snapshot() : null),
    styleLabel: () => STYLES[state.styleId].label,
    materialLabel: () => MATERIALS[state.material].label,
    translateText: (source) => language.lines(source),
    translateHtml: (source) => language.html(source),
  });

  // ---- starter examples -------------------------------------------------------------------
  function renderExamples() {
    els.examples.replaceChildren(...EXAMPLES.map((example) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'wr-l3d__chip-btn';
      b.textContent = example.label;
      b.title = example.hint;
      b.dataset.l3dExample = example.id;
      b.addEventListener('click', () => {
        if (example.neon && state.source !== 'neon') { ui.styleBeforeNeon = state.styleId; ui.paramsBeforeNeon = state.params; } // to come back to when the neon is left
        Object.assign(state, applyExample(state, example.id));
        syncForm();
        scheduleBuild(0);
        toast('Cargamos un ejemplo. Cambiá el texto y los ajustes a tu gusto.');
        track('l3d_example', { id: example.id });
      });
      return b;
    }));
  }

  // ---- projects: the whole design, logo included, in a file ----------------------------------
  const PROJECT_ERRORS = {
    ERR_PROJECT_EMPTY: 'Ese archivo está vacío.',
    ERR_PROJECT_TOO_BIG: 'Ese archivo pesa demasiado para ser un proyecto.',
    ERR_PROJECT_NOT_JSON: 'Ese archivo no es un proyecto del generador.',
    ERR_PROJECT_NOT_A_PROJECT: 'Ese archivo no es un proyecto del generador.',
    ERR_PROJECT_NEWER: 'Ese proyecto se guardó con una versión más nueva del generador.',
  };

  function saveProject() {
    download(`proyecto-${slug(designName())}.json`, encoder.encode(serializeProject(state, { quote: costs.quoteData() })), 'application/json');
    toast('Proyecto guardado. Abrilo cuando quieras con «Abrir proyecto».');
    track('l3d_project_save', { source: state.source });
  }

  /** Opens a project from its text (a file or a saved project): true when it was valid. */
  function applyProjectText(text) {
    const { project, quote, error } = parseProject(text);
    if (error) { toast(PROJECT_ERRORS[error] ?? PROJECT_ERRORS.ERR_PROJECT_NOT_A_PROJECT); return false; }
    const notes = [];
    if (project.svg) {
      try {
        drawingToPieces(project.svg, { heightMm: project.heightMm, whiteAsHole: project.whiteAsHole }); // validate before replacing what is on screen
      } catch {
        project.svg = null;
        project.source = 'text';
        notes.push('No pudimos leer el dibujo del proyecto: abrimos el resto.');
      }
    }
    if (project.fontId === CUSTOM_FONT_ID && !fonts.has(CUSTOM_FONT_ID)) notes.push('La tipografía propia no viaja en el archivo: usamos otra.');
    Object.assign(state, project);
    reconcileStyle();
    nodeEditor.history = null;
    nodeEditor.selected = null;
    if (quote) costs.loadQuoteData(quote);
    syncForm();
    scheduleBuild(0);
    toast(['Proyecto abierto.', ...notes]);
    track('l3d_project_open', { source: state.source });
    return true;
  }

  async function openProject(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast(PROJECT_ERRORS.ERR_PROJECT_TOO_BIG); return; }
    applyProjectText(await file.text());
  }

  // ---- clearance test kit ---------------------------------------------------------------------
  async function downloadKit() {
    if (!kitSupported(state.styleId)) return;
    try {
      const { font } = await ensureFont(DEFAULT_FONT_ID); // the numbers on the pieces: a bold, plain face
      const labeler = (text, heightMm) => textToPieces(font, text, { heightMm }).pieces;
      const kit = buildClearanceKit({ styleId: state.styleId, params: state.params, labeler });
      const files = [];
      for (const part of kit.parts) {
        if (part.kind === 'print') {
          files.push({ name: `STL/${KIT_FILES[part.id]}.stl`, data: toBinaryStl(orientForPrint(layersToTriangles(part.layers), part.id, { bed: state.bed })) });
        } else {
          const region = part.layers.flatMap((layer) => layer.region);
          files.push({ name: `Acrilico/${KIT_FILES.acrilico}.dxf`, data: encoder.encode(toDxf([{ layer: 'ACRILICO', color: 1, region }])) });
          files.push({ name: `Acrilico/${KIT_FILES.acrilico}.svg`, data: encoder.encode(toSvg(region, { widthMm: kit.widthMm, heightMm: kit.heightMm })) });
        }
      }
      files.push({ name: readmeName(), data: encoder.encode(language.lines(buildKitReadme({ kit, styleLabel: STYLES[state.styleId].label }))) });
      download('kit-prueba-holgura.zip', createZip(files), 'application/zip');
      track('l3d_download_kit', { style: state.styleId });
    } catch (error) {
      console.error(error);
      problems.push(`kit: ${error?.message || error}`);
      toast('No pudimos armar el kit de prueba. Probá de nuevo.');
    }
  }

  const library = createLibrary({
    $,
    toast,
    track,
    getProjectText: () => serializeProject(state, { quote: costs.quoteData() }),
    applyProjectText,
    snapshot: () => (viewer ? viewer.snapshot() : null),
    designName: () => designName(),
    styleId: () => state.styleId,
    styleLabel: (id) => STYLES[id]?.label ?? '',
  });
  const tour = createTour({ host, $, $$, track });

  function bindProjects() {
    els.kit.addEventListener('click', downloadKit);
    els.projectSave.addEventListener('click', saveProject);
    els.projectOpen.addEventListener('click', () => els.projectFile.click());
    els.projectFile.addEventListener('change', () => { const file = els.projectFile.files?.[0]; els.projectFile.value = ''; openProject(file); });
  }

  // ---- links to War Record --------------------------------------------------------------
  function updateLinks() {
    const text = designName();
    const cm = nf1.format(state.heightMm / 10);
    const style = STYLES[state.styleId].label;
    const shareUrl = `${location.origin}${location.pathname}#d=${encodeState(shareableState())}`;
    const digits = (host.dataset.whatsapp || '').replace(/\D/g, '');
    const message = text
      ? `Hola War Record! Armé este diseño en el generador de letras 3D: «${text}», ${cm} cm de alto, estilo ${style}. Lo podés ver acá: ${shareUrl} . Quiero cotizar la fabricación.`
      : 'Hola War Record! Quiero cotizar unas letras 3D.';
    els.wa.href = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;

    const material = STYLES[state.styleId].quoteMaterial;
    const base = host.dataset.quoteUrl || '/pages/cotizador';
    const query = new URLSearchParams({ modo: 'letrismo', texto: text, alto: String(state.heightMm / 10), mat: material, origen: 'generador-letras-3d' });
    els.quote.href = `${base}${base.includes('?') ? '&' : '?'}${query}`;
  }

  function shareableState() {
    return { ...state, params: { ...state.params } };
  }

  function schedulePersist() {
    clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      const encoded = encodeState(shareableState());
      writeHash(encoded);
      try {
        localStorage.setItem('wr-l3d:last', encoded);
        localStorage.setItem('wr-l3d:source', state.source);
        // A logo is kept only when it is small enough for the browser's storage; otherwise the next visit starts with text.
        if (state.svg && state.svg.text.length < 700_000) localStorage.setItem('wr-l3d:svg', JSON.stringify({ ...state.svg, whiteAsHole: state.whiteAsHole }));
        if (state.neon && state.neon.text.length < 700_000) localStorage.setItem('wr-l3d:neon', JSON.stringify(state.neon));
        if (state.curves) {
          const saved = JSON.stringify(state.curves);
          if (saved.length < 700_000) localStorage.setItem('wr-l3d:curves', saved);
        }
      } catch { /* storage may be blocked or full */ }
      updateLinks();
    }, 400);
  }

  // ---- downloads ----------------------------------------------------------------------------
  const PART_FILE = { tapa: 'Tapa', base: 'Base', cuerpo: 'Letra', canal: 'Canal' };

  /** One STL per printed part; a piece that was cut gets one per tile, named Tapa_03B.stl (letter 3, tile B). */
  function printFiles() {
    const files = [];
    const printed = model.pieces.flatMap((piece) => piece.parts.filter((p) => p.kind === 'print' && !p.accessory));
    const ids = [...new Set(printed.map((p) => p.id))]; // Tapa first, then Base, in the order the style lists them
    for (const id of ids) {
      for (const piece of model.pieces) {
        for (const part of piece.parts) {
          if (part.kind !== 'print' || part.accessory || part.id !== id) continue;
          files.push({
            name: `STL/${PART_FILE[id] ?? id}_${String(piece.index + 1).padStart(2, '0')}${part.tile ?? ''}.stl`,
            data: toBinaryStl(orientForPrint(part.positions, id, { bed: state.bed })),
          });
        }
      }
    }
    for (const [index, plate] of standoffPrint().plates.entries()) {
      files.push({
        name: `STL/Separadores_${String(index + 1).padStart(2, '0')}_x${plate.count}.stl`,
        data: toBinaryStl(orientForPrint(layersToTriangles(plate.layers), 'separadores', { bed: state.bed })),
      });
    }
    return files;
  }

  /** The wall spacers of the whole sign, laid out on plates for the printer: how many there are and the plates. */
  function standoffPrint() {
    const count = model.pieces.reduce((sum, piece) => sum + (piece.parts.find((part) => part.accessory)?.spots.length ?? 0), 0);
    if (count === 0) return { count: 0, plates: [] };
    const { standoffDiameter: diameter, standoffHeight: height, standoffHole: hole } = model.params;
    return { count, plates: standoffPlates({ count, diameter, height, hole, bed: state.bed }) };
  }

  const wasSplit = () => (model?.splitInfo?.pieces ?? 0) > 0;

  // Parts cut from sheet, one file pair per material: acrylic and, in the PVC style, the back panel.
  const CUT_FILES = {
    acrilico: { folder: 'Acrilico', file: 'Acrilico', layer: 'ACRILICO', color: 1, label: 'acrílico', what: 'el contorno del acrílico', download: 'acrilico' },
    fondo: { folder: 'PVC', file: 'Fondo_PVC', layer: 'PVC', color: 5, label: 'PVC', what: 'el contorno del fondo de PVC', download: 'fondo-pvc' },
  };

  function cutGroups() {
    const groups = [];
    for (const [id, spec] of Object.entries(CUT_FILES)) {
      // one outline per letter: its parts travel together when the pieces are laid out on a sheet
      const items = model.pieces
        .map((piece) => ({ id: piece.index + 1, region: piece.parts.filter((p) => p.kind === 'cut' && p.id === id).flatMap((p) => p.layers.flatMap((l) => l.region)) }))
        .filter((item) => item.region.length > 0);
      const region = items.flatMap((item) => item.region);
      if (region.length) groups.push({ id, ...spec, region, items });
    }
    return groups;
  }

  /** Every group of pieces to cut, laid out on the maker's sheet. */
  const sheetLayouts = () => cutGroups().map((group) => ({ group, layout: nestPieces(group.items, state.sheet) }));

  /** One DXF (the sheet as a reference layer plus the pieces) and one SVG per sheet. */
  const sheetFiles = ({ group, layout }) => layout.sheets.flatMap((sheet) => {
    const name = `${group.folder}/Planchas/Plancha_${String(sheet.index).padStart(2, '0')}`;
    const dxf = toDxf([
      { layer: 'PLANCHA', color: 8, region: sheetOutline(layout.sheet) },
      { layer: group.layer, color: group.color, region: sheet.region },
    ]);
    return [
      { name: `${name}.dxf`, data: encoder.encode(dxf) },
      { name: `${name}.svg`, data: encoder.encode(toSheetSvg(sheet.region, layout.sheet)) },
    ];
  });

  const cutDxf = (group) => toDxf([{ layer: group.layer, color: group.color, region: group.region }]);
  const cutSvg = (group) => toSvg(group.region, { widthMm: model.widthMm, heightMm: model.heightMm });
  const encoder = new TextEncoder();

  function readme() {
    const entry = fonts.get(state.fontId);
    return buildReadme({
      text: designName(),
      fontLabel: state.source === 'svg' ? 'No aplica (es un logo)' : (entry?.label ?? state.fontId),
      styleLabel: STYLES[state.styleId].label,
      params: model.params,
      widthMm: model.widthMm,
      heightMm: model.heightMm,
      depthMm: model.depthMm,
      pieceCount: model.pieces.length,
      styleId: state.styleId,
      hasAcrylic: cutGroups().length > 0,
      cutParts: sheetLayouts().map(({ group, layout }) => ({
        folder: group.folder,
        what: group.what,
        sheets: layout.sheets.length ? { count: layout.sheets.length, width: layout.sheet.width, height: layout.sheet.height } : undefined,
      })),
      splitInfo: wasSplit() ? { ...model.splitInfo, joint: state.split.joint } : null,
      standoffs: standoffPrint().count ? { count: standoffPrint().count, plates: standoffPrint().plates.length } : null,
      manualHoles: model.holes,
      neon: model.neon ? { lengthMm: model.neon.lengthMm, ends: model.neon.ends } : null,
    });
  }

  function bindActions() {
    const downloadZip = () => {
      if (!model) return;
      const files = printFiles();
      for (const group of cutGroups()) {
        files.push({ name: `${group.folder}/${group.file}.dxf`, data: encoder.encode(cutDxf(group)) });
        files.push({ name: `${group.folder}/${group.file}.svg`, data: encoder.encode(cutSvg(group)) });
      }
      for (const entry of sheetLayouts()) files.push(...sheetFiles(entry));
      if (wasSplit()) files.push({ name: 'Plano_de_tramos.svg', data: encoder.encode(toAssemblySvg(model)) });
      files.push({ name: readmeName(), data: encoder.encode(language.lines(readme())) });
      download(`letras-3d-${slug(designName())}.zip`, createZip(files), 'application/zip');
      track('l3d_download_zip', { style: state.styleId, pieces: model.pieces.length });
    };
    for (const button of els.zips) button.addEventListener('click', downloadZip);
    els.stl.addEventListener('click', () => {
      if (!model) return;
      download(`letras-3d-${slug(designName())}-stl.zip`, createZip(printFiles()), 'application/zip');
      track('l3d_download_stl', { style: state.styleId });
    });
    els.sheetsZip.addEventListener('click', () => {
      if (!model) return;
      const files = sheetLayouts().flatMap(sheetFiles);
      if (files.length === 0) return;
      download(`planchas-${slug(designName())}.zip`, createZip(files), 'application/zip');
      track('l3d_download_sheets', { style: state.styleId, sheets: files.length / 2 });
    });
    const downloadCut = (id, kind) => {
      if (!model) return;
      const group = cutGroups().find((g) => g.id === id);
      if (!group) return;
      const dxf = kind === 'dxf';
      download(`${group.download}-${slug(designName())}.${kind}`, encoder.encode(dxf ? cutDxf(group) : cutSvg(group)), dxf ? 'application/dxf' : 'image/svg+xml');
      track(`l3d_download_${kind}`, { part: id });
    };
    els.dxf.addEventListener('click', () => downloadCut('acrilico', 'dxf'));
    els.svg.addEventListener('click', () => downloadCut('acrilico', 'svg'));
    els.dxfBack.addEventListener('click', () => downloadCut('fondo', 'dxf'));
    els.svgBack.addEventListener('click', () => downloadCut('fondo', 'svg'));
    els.quote.addEventListener('click', () => track('l3d_click_quote', { style: state.styleId }));
    els.wa.addEventListener('click', () => track('l3d_click_whatsapp', { style: state.styleId }));
    els.copy.addEventListener('click', async () => {
      clearTimeout(persistTimer);
      const encoded = encodeState(shareableState());
      writeHash(encoded);
      const url = `${location.origin}${location.pathname}${language.lang === 'es' ? '' : `?lang=${language.lang}`}#d=${encoded}`;
      if (await copyText(url)) toast(state.source === 'svg' ? 'Enlace copiado. No incluye el logo: mandá también el archivo SVG.' : 'Enlace copiado.');
      else toast('No pudimos copiarlo. Copialo de la barra del navegador.');
      track('l3d_copy_link');
    });
  }

  // ---- stage controls ---------------------------------------------------------------------------
  function setView(view) {
    ui.view = view;
    host.dataset.view = view;
    for (const tab of els.tabs) tab.setAttribute('aria-pressed', String(tab.dataset.l3dTab === view));
    // toggleAttribute, not .hidden: SVG elements have no `hidden` property, so it would silently do nothing.
    els.canvas.toggleAttribute('hidden', view !== '3d');
    els.glow.toggleAttribute('hidden', view !== '3d');
    els.plan.toggleAttribute('hidden', view !== 'plan');
    if (view === '3d') viewer?.refresh();
  }

  // ---- node editor ---------------------------------------------------------------------------------------
  const NODE_DRAW_LIMIT = 3000; // past this many nodes the handles would bury the drawing: simplify first
  const nodeEditor = { history: null, selected: null, drag: null, layerTimer: 0 };
  const inNodeMode = () => state.source === 'curves' && Boolean(state.curves);
  const nodeKey = (ref) => `${ref.piece}:${ref.ring}:${ref.index}`;
  const pathOfPiece = (piece) => [piece.outer, ...piece.holes].map(ringPath).join('');

  /** Millimetres of the plan that one pixel of the screen covers right now. */
  function mmPerPixel() {
    const width = els.plan.getBoundingClientRect().width;
    return plan.box && width > 0 ? plan.box.w / width : 1;
  }

  /** A pointer position as a point of the sign, in millimetres (the plan draws y downwards). */
  function planMm(event) {
    const at = planPoint(event);
    return { x: at.x, y: model.heightMm - at.y };
  }

  function ensureHistory() {
    if (state.curves && (!nodeEditor.history || nodeEditor.history.present !== state.curves.pieces)) nodeEditor.history = newHistory(state.curves.pieces);
  }

  function renderNodeLayer() {
    const layer = els.plan.querySelector('[data-node-layer]');
    if (!layer) return;
    if (!inNodeMode()) { layer.replaceChildren(); return; }
    const pieces = state.curves.pieces;
    if (countNodesIn(pieces) > NODE_DRAW_LIMIT) { layer.replaceChildren(); renderNodeStats(); return; }
    const radius = (4.5 * mmPerPixel()).toFixed(3);
    const selected = nodeEditor.selected ? nodeKey(nodeEditor.selected) : '';
    const marks = [];
    pieces.forEach((piece, p) => {
      [piece.outer, ...piece.holes].forEach((ring, r) => {
        ring.forEach(([x, y], index) => {
          const ref = { piece: p, ring: r === 0 ? -1 : r - 1, index };
          marks.push(`<circle class="node${nodeKey(ref) === selected ? ' is-selected' : ''}" data-node="${nodeKey(ref)}" vector-effect="non-scaling-stroke" cx="${x}" cy="${y}" r="${radius}"/>`);
        });
      });
    });
    layer.innerHTML = marks.join('');
    renderNodeStats();
  }

  function scheduleNodeLayer() {
    if (!inNodeMode()) return;
    clearTimeout(nodeEditor.layerTimer);
    nodeEditor.layerTimer = setTimeout(renderNodeLayer, 120);
  }

  function renderNodeStats() {
    const has = inNodeMode();
    if (!has) return;
    const pieces = state.curves.pieces;
    const total = countNodesIn(pieces);
    const picked = nodeEditor.selected ? listNodeAt(pieces, nodeEditor.selected) : null;
    setSentences(els.nodeStats, [
      `${nf0.format(total)} nodos en ${nf0.format(pieces.length)} ${pieces.length === 1 ? 'pieza' : 'piezas'}.`,
      total > NODE_DRAW_LIMIT ? 'Son demasiados para mostrarlos todos: tocá «Simplificar ahora».' : '',
      picked ? `Nodo elegido: x ${nf1.format(picked[0])} mm, y ${nf1.format(picked[1])} mm.` : '',
    ]);
    ensureHistory();
    els.nodeUndo.disabled = nodeEditor.history.past.length === 0;
    els.nodeRedo.disabled = nodeEditor.history.future.length === 0;
    els.nodeDelete.disabled = !picked;
    els.nodeAdd.setAttribute('aria-pressed', String(ui.addNode));
  }

  function listNodeAt(pieces, ref) {
    const piece = pieces[ref.piece];
    const ring = piece ? (ref.ring === -1 ? piece.outer : piece.holes[ref.ring]) : null;
    return ring?.[ref.index] ?? null;
  }

  function selectNode(ref) {
    nodeEditor.selected = ref;
    for (const mark of els.plan.querySelectorAll('.node.is-selected')) mark.classList.remove('is-selected');
    if (ref) els.plan.querySelector(`[data-node="${nodeKey(ref)}"]`)?.classList.add('is-selected');
    renderNodeStats();
  }

  /** Makes `next` the drawing: a step of the history, the plan and the 3D model follow. */
  function commitNodes(next, selected = null) {
    ensureHistory();
    nodeEditor.history = pushHistory(nodeEditor.history, next);
    state.curves = { ...state.curves, pieces: nodeEditor.history.present };
    nodeEditor.selected = selected;
    if (model) renderPlan();
    scheduleBuild(250);
    schedulePersist();
    track('l3d_nodes_edit', { nodes: countNodesIn(next) });
  }

  function stepHistory(direction) {
    ensureHistory();
    const next = direction === 'undo' ? undoHistory(nodeEditor.history) : redoHistory(nodeEditor.history);
    if (next === nodeEditor.history) return;
    nodeEditor.history = next;
    state.curves = { ...state.curves, pieces: next.present };
    nodeEditor.selected = null;
    if (model) renderPlan();
    scheduleBuild(250);
    schedulePersist();
  }

  function deleteSelected() {
    if (!nodeEditor.selected) return;
    const result = deleteNode(state.curves.pieces, nodeEditor.selected);
    if (!result.deleted) { toast(result.reason); return; }
    commitNodes(result.pieces);
  }

  function addNodeAt(point) {
    const edge = nearestEdge(state.curves.pieces, point.x, point.y, 14 * mmPerPixel());
    if (!edge) { toast('Tocá justo sobre el borde del dibujo para agregar un nodo.'); return false; }
    const result = insertNode(state.curves.pieces, edge, edge.x, edge.y);
    commitNodes(result.pieces, result.at);
    return true;
  }

  function nudgeSelected(dx, dy) {
    const ref = nodeEditor.selected;
    const at = ref && listNodeAt(state.curves.pieces, ref);
    if (!at) return;
    commitNodes(moveNode(state.curves.pieces, ref, at[0] + dx, at[1] + dy), ref);
  }

  /** Turns the design on screen into curves and shows them on the plan, or goes back to the curves already edited. */
  function enterCurves() {
    const from = state.source;
    if (!state.curves) {
      if (!model) { toast('Primero hacé un texto o cargá un logo para poder editarlo.'); return; }
      state.curves = fromPieces(model.pieces.map((piece) => piece.silhouette), model.widthMm, model.heightMm);
      nodeEditor.history = newHistory(state.curves.pieces);
    }
    if (from !== 'curves') ui.curvesOrigin = from;
    state.source = 'curves';
    nodeEditor.selected = null;
    ui.holeMode = false;
    renderHolePanel();
    applySource();
    setView('plan');
    if (model) renderPlan();
    scheduleBuild(0);
    track('l3d_nodes_enter', { from });
  }

  function bindNodes() {
    els.nodeEnter.addEventListener('click', enterCurves);
    els.nodeUndo.addEventListener('click', () => stepHistory('undo'));
    els.nodeRedo.addEventListener('click', () => stepHistory('redo'));
    els.nodeDelete.addEventListener('click', deleteSelected);
    els.nodeAdd.addEventListener('click', () => {
      ui.addNode = !ui.addNode;
      els.plan.classList.toggle('is-adding', ui.addNode);
      renderNodeStats();
      if (ui.addNode) toast('Tocá el borde del dibujo donde quieras el nodo nuevo.');
    });
    els.nodeSimplify.addEventListener('click', () => {
      if (!inNodeMode()) return;
      const settings = normalizeOptimize(state.optimize);
      const result = optimizePieces(state.curves.pieces, { tolerance: settings.tolerance, speck: settings.speck });
      if (result.stats.nodesAfter >= result.stats.nodesBefore && result.stats.specks === 0) { toast('Ya está tan simple como permite la tolerancia.'); return; }
      commitNodes(result.pieces);
      toast(`Quedaron ${nf0.format(result.stats.nodesAfter)} nodos (había ${nf0.format(result.stats.nodesBefore)}).`);
    });
    let armed = false;
    els.nodeRestart.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        els.nodeRestart.textContent = 'Tocá de nuevo para descartar las curvas';
        setTimeout(() => { armed = false; els.nodeRestart.textContent = 'Descartar las curvas y empezar de nuevo'; }, 3500);
        return;
      }
      armed = false;
      els.nodeRestart.textContent = 'Descartar las curvas y empezar de nuevo';
      state.curves = null;
      state.source = ui.curvesOrigin === 'svg' && state.svg ? 'svg' : 'text';
      nodeEditor.history = null;
      nodeEditor.selected = null;
      applySource();
      scheduleBuild(0);
      schedulePersist();
    });
  }

  // ---- curve optimizer ---------------------------------------------------------------------------------
  function syncOptimize() {
    els.optOn.checked = state.optimize.on;
    els.optTol.value = String(state.optimize.tolerance);
    els.optSpeck.value = String(state.optimize.speck);
    els.optCompare.checked = ui.compare;
    els.optFields.hidden = !state.optimize.on;
    els.optCompareRow.hidden = !state.optimize.on;
  }

  function renderOptimizeStats() {
    if (!model) { els.optStats.textContent = ''; return; }
    const info = model.optimization;
    if (!info) {
      setSentences(els.optStats, [`El dibujo tiene ${nf0.format(model.nodes)} nodos.`, model.nodes > HEAVY_NODES ? 'Activá la simplificación para aliviarlo.' : '']);
      return;
    }
    const { stats } = info;
    const saved = stats.nodesBefore > 0 ? Math.round((1 - (stats.nodesAfter / stats.nodesBefore)) * 100) : 0;
    const gone = stats.specks > 0 ? ` y ${stats.specks === 1 ? 'una mancha quitada' : `${nf0.format(stats.specks)} manchas quitadas`}` : '';
    setSentences(els.optStats, [
      `Antes: ${nf0.format(stats.nodesBefore)} nodos en ${nf0.format(stats.piecesBefore)} ${stats.piecesBefore === 1 ? 'pieza' : 'piezas'}.`,
      `Después: ${nf0.format(stats.nodesAfter)} nodos en ${nf0.format(stats.piecesAfter)} ${stats.piecesAfter === 1 ? 'pieza' : 'piezas'} (${saved}% menos${gone}).`,
    ]);
  }

  function bindOptimize() {
    const change = () => { syncOptimize(); scheduleBuild(0); };
    els.optOn.addEventListener('change', () => { state.optimize = { ...state.optimize, on: els.optOn.checked }; track('l3d_optimize', { on: state.optimize.on }); change(); });
    els.optTol.addEventListener('input', () => {
      state.optimize = normalizeOptimize({ ...state.optimize, tolerance: toNumber(els.optTol.value, OPTIMIZE_LIMITS.tolerance.default) });
      scheduleBuild(300);
    });
    els.optSpeck.addEventListener('input', () => {
      state.optimize = normalizeOptimize({ ...state.optimize, speck: toNumber(els.optSpeck.value, OPTIMIZE_LIMITS.speck.default) });
      scheduleBuild(300);
    });
    for (const input of [els.optTol, els.optSpeck]) {
      input.addEventListener('change', () => { els.optTol.value = String(state.optimize.tolerance); els.optSpeck.value = String(state.optimize.speck); });
    }
    els.optCompare.addEventListener('change', () => {
      ui.compare = els.optCompare.checked;
      if (ui.compare) setView('plan');
      if (model) renderPlan();
    });
  }

  // ---- cuts of split letters: dragged on the plan, or typed --------------------------------------------
  const cutName = (cut) => `${cut.axis === 'x' ? 'vertical' : 'horizontal'} ${cut.index + 1}`;

  /** Moves one cut of one letter (clamped to where every tile still fits the bed) and re-splits. */
  function setCut(pieceIndex, axis, index, wanted) {
    const piece = model?.pieces[pieceIndex];
    const cut = piece?.cuts?.find((c) => c.axis === axis && c.index === index);
    if (!cut || !Number.isFinite(wanted)) return;
    const at = clamp(Math.round(wanted * 2) / 2, cut.lo, cut.hi);
    if (Math.abs(at - cut.at) < 1e-6) { renderCutList(); return; }
    const positions = (a) => piece.cuts.filter((c) => c.axis === a).sort((p, q) => p.index - q.index).map((c) => c.at);
    const xs = positions('x');
    const ys = positions('y');
    (axis === 'x' ? xs : ys)[index] = at;
    const b = piece.bounds;
    const entry = { i: piece.index, b: [b.minX, b.minY, b.maxX, b.maxY], x: xs, y: ys };
    state.split.cuts = normalizeCuts([...state.split.cuts.filter((c) => c.i !== piece.index), entry]);
    track('l3d_cut_moved', { axis });
    scheduleBuild(0);
  }

  /** What a cut position means for the maker: the two tiles it leaves and how much material it crosses. */
  function describeCut(pieceIndex, axis, index, at) {
    const piece = model.pieces[pieceIndex];
    const others = piece.cuts.filter((c) => c.axis === axis).sort((p, q) => p.index - q.index).map((c) => c.at);
    others[index] = at;
    const [lowest, highest] = axis === 'x' ? [piece.bounds.minX, piece.bounds.maxX] : [piece.bounds.minY, piece.bounds.maxY];
    const edges = [lowest, ...others, highest];
    const crossed = cutCost([piece.silhouette], axis, at).length;
    return `Tramos de ${nf0.format(edges[index + 1] - edges[index])} y ${nf0.format(edges[index + 2] - edges[index + 1])} mm. El corte cruza ${nf0.format(crossed)} mm de material.`;
  }

  function renderCutList() {
    const split = model?.pieces.filter((piece) => piece.cuts?.length) ?? [];
    els.cutsBox.hidden = split.length === 0;
    els.cutsResetRow.hidden = !split.some((piece) => piece.customCuts);
    els.cutList.replaceChildren(...split.flatMap((piece) => piece.cuts.map((cut) => {
      const li = document.createElement('li');
      const label = document.createElement('label');
      label.textContent = `Letra ${piece.index + 1}, corte ${cutName(cut)}`;
      const input = document.createElement('input');
      input.className = 'wr-l3d__input';
      input.type = 'number';
      input.inputMode = 'decimal';
      input.step = '0.5';
      input.min = String(Math.ceil(cut.lo * 2) / 2);
      input.max = String(Math.floor(cut.hi * 2) / 2);
      input.value = String(Math.round(cut.at * 10) / 10);
      input.setAttribute('aria-label', `Posición del corte ${cutName(cut)} de la letra ${piece.index + 1}, en milímetros desde ${cut.axis === 'x' ? 'la izquierda' : 'abajo'}`);
      input.addEventListener('change', () => setCut(piece.index, cut.axis, cut.index, toNumber(input.value, cut.at)));
      li.append(label, input);
      return li;
    })));
  }

  // ---- holes placed by hand ---------------------------------------------------------------------------
  const HOLE_STATE_TEXT = {
    blocked: 'Cae sobre la pared o el enchufe: no se hace. Movelo más adentro.',
    outside: 'Cayó fuera de las letras: no se hace.',
  };
  const nfHole = numberFormat({ maximumFractionDigits: 1 });

  function renderHolePanel() {
    const reported = model?.holes?.length === state.holes.length ? model.holes : state.holes.map((hole) => ({ ...hole, status: 'ok', pieceIndex: null }));
    els.holeList.replaceChildren(...reported.map((hole, index) => {
      const li = document.createElement('li');
      li.className = `is-${hole.status}`;
      const text = document.createElement('span');
      text.textContent = `${index + 1}. x ${nfHole.format(hole.x)} · y ${nfHole.format(hole.y)} · Ø ${nfHole.format(hole.d)} mm${hole.pieceIndex != null ? ` · letra ${hole.pieceIndex + 1}` : ''}`;
      if (HOLE_STATE_TEXT[hole.status]) {
        const note = document.createElement('span');
        note.className = 'wr-l3d__hole-state';
        note.textContent = HOLE_STATE_TEXT[hole.status];
        text.append(note);
      }
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'wr-l3d__hole-remove';
      remove.textContent = 'Quitar';
      remove.setAttribute('aria-label', `Quitar el agujero ${index + 1}`);
      remove.addEventListener('click', () => { state.holes = removeHole(state.holes, index); holesChanged(); });
      li.append(text, remove);
      return li;
    }));
    els.holeEmpty.hidden = state.holes.length > 0;
    els.holeClearRow.hidden = state.holes.length === 0;
    els.holeMode.setAttribute('aria-pressed', String(ui.holeMode));
    els.holeMode.textContent = ui.holeMode ? 'Listo, dejar de marcar' : 'Marcar en la planta';
    els.plan.classList.toggle('is-marking', ui.holeMode);
    els.holeD.value = String(ui.holeDiameter);
  }

  function holesChanged() {
    if (model) { // the plan answers at once; the rebuild that follows says which holes really fit
      model.holes = state.holes.map((hole) => ({ ...hole, status: 'ok', pieceIndex: null }));
      renderPlan();
    }
    renderHolePanel();
    scheduleBuild(0);
    track('l3d_holes', { count: state.holes.length });
  }

  /** Puts a hole at (x, y) if its letter can take it there; tells the maker why not otherwise. */
  function tryAddHole(x, y) {
    if (!model) return false;
    const hole = { x, y, d: ui.holeDiameter };
    const owner = model.pieces.find((piece) => pointInRegion([piece.silhouette], hole.x, hole.y));
    if (!owner) { toast('Tocá sobre una letra para marcar el agujero.'); return false; }
    if (!manualHoleFits(manualHoleZone(state.styleId, owner.silhouette, model.params), hole)) {
      toast(state.styleId === 'maciza'
        ? 'Ahí queda muy cerca del borde de la letra: probá más adentro.'
        : 'Ahí cae la pared o el enchufe de la letra: probá más adentro, sobre el piso.');
      return false;
    }
    const next = addHole(state.holes, hole);
    if (!next.added) {
      toast(state.holes.length >= HOLE_LIMITS.count ? `Llegaste al máximo de ${HOLE_LIMITS.count} agujeros.` : 'Ya hay un agujero ahí.');
      return false;
    }
    state.holes = next.holes;
    holesChanged();
    return true;
  }

  /** A tap on the plan while marking: on a hole it removes it, anywhere else it adds one. */
  function tapPlan(event) {
    if (!model || !plan.box) return;
    const at = planPoint(event);
    const x = at.x;
    const y = model.heightMm - at.y; // the plan draws y downwards
    const index = holeIndexAt(state.holes, x, y, plan.box.w / 90);
    if (index >= 0) {
      state.holes = removeHole(state.holes, index);
      holesChanged();
    } else {
      tryAddHole(x, y);
    }
  }

  function bindHoles() {
    const setDiameter = (value) => {
      const d = toNumber(value, HOLE_LIMITS.default);
      ui.holeDiameter = clamp(Math.round(d / HOLE_LIMITS.step) * HOLE_LIMITS.step, HOLE_LIMITS.min, HOLE_LIMITS.max);
    };
    els.holeD.addEventListener('input', () => setDiameter(els.holeD.value));
    els.holeD.addEventListener('change', () => { setDiameter(els.holeD.value); els.holeD.value = String(ui.holeDiameter); });
    for (const button of els.holePresets) {
      button.addEventListener('click', () => { setDiameter(button.dataset.l3dHolePreset); els.holeD.value = String(ui.holeDiameter); });
    }
    els.holeMode.addEventListener('click', () => {
      ui.holeMode = !ui.holeMode;
      if (ui.holeMode) {
        setView('plan');
        toast('Tocá una letra para marcar un agujero. Tocá un agujero para quitarlo.');
      }
      renderHolePanel();
    });
    els.holeAdd.addEventListener('click', () => {
      const x = toNumber(els.holeX.value, NaN);
      const y = toNumber(els.holeY.value, NaN);
      if (!Number.isFinite(x) || !Number.isFinite(y)) { toast('Escribí las dos medidas: desde la izquierda y desde abajo.'); return; }
      if (tryAddHole(x, y)) { els.holeX.value = ''; els.holeY.value = ''; }
    });
    let armed = false;
    els.holeClear.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        els.holeClear.textContent = 'Tocá de nuevo para borrar todos';
        setTimeout(() => { armed = false; els.holeClear.textContent = 'Borrar todos los agujeros'; }, 3500);
        return;
      }
      armed = false;
      els.holeClear.textContent = 'Borrar todos los agujeros';
      state.holes = [];
      holesChanged();
    });
  }

  // ---- plan view: wheel/pinch zoom, drag to pan, double click to reset -------------------------------------
  const plan = { home: null, box: null, zoomed: false };

  function applyPlanBox() {
    const { x, y, w, h } = plan.box;
    els.plan.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
    scheduleNodeLayer();
  }

  /** A new drawing frames itself, unless the user zoomed in and the drawing barely changed size. */
  function setPlanHome(x, y, w, h) {
    const previous = plan.home;
    plan.home = { x, y, w, h };
    const similar = previous && Math.abs(w - previous.w) / previous.w < 0.15 && Math.abs(h - previous.h) / previous.h < 0.15;
    if (!(plan.zoomed && similar)) {
      plan.box = { ...plan.home };
      plan.zoomed = false;
    }
    applyPlanBox();
  }

  function planPoint(event) {
    const ctm = els.plan.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
    return { x: point.x, y: point.y };
  }

  function zoomPlan(factor, center) {
    if (!plan.box) return;
    const { x, y, w, h } = plan.box;
    const nextW = clamp(w / factor, plan.home.w / 60, plan.home.w * 4);
    const nextH = nextW * (h / w);
    const c = center ?? { x: x + (w / 2), y: y + (h / 2) };
    plan.box = { x: c.x - (((c.x - x) * nextW) / w), y: c.y - (((c.y - y) * nextH) / h), w: nextW, h: nextH };
    plan.zoomed = true;
    applyPlanBox();
  }

  function resetPlan() {
    if (!plan.home) return;
    plan.box = { ...plan.home };
    plan.zoomed = false;
    applyPlanBox();
  }

  function bindPlanNavigation() {
    const svg = els.plan;
    const pointers = new Map();
    let pinch = 0;
    let tap = null; // the press that may turn out to be a tap
    let cutDrag = null; // a cut line being moved
    let nodeDrag = null; // a node being moved
    svg.addEventListener('wheel', (event) => {
      event.preventDefault();
      zoomPlan(Math.exp(-event.deltaY * 0.0015), planPoint(event));
    }, { passive: false });
    svg.addEventListener('pointerdown', (event) => {
      const hit = event.target.closest?.('[data-cut]');
      if (hit && !ui.holeMode) { // a cut line: move it instead of panning the plan
        svg.setPointerCapture(event.pointerId);
        cutDrag = {
          id: event.pointerId, piece: Number(hit.dataset.piece), axis: hit.dataset.axis, index: Number(hit.dataset.index),
          lo: Number(hit.dataset.lo), hi: Number(hit.dataset.hi), at: null,
        };
        event.preventDefault();
        return;
      }
      if (inNodeMode() && model && !ui.holeMode && pointers.size === 0) {
        const point = planMm(event);
        if (ui.addNode) { // the next touch on an edge puts a node there
          if (addNodeAt(point)) { ui.addNode = false; renderNodeStats(); }
          event.preventDefault();
          return;
        }
        const hit = nearestNode(state.curves.pieces, point.x, point.y, 11 * mmPerPixel());
        if (hit) {
          svg.setPointerCapture(event.pointerId);
          const ref = { piece: hit.piece, ring: hit.ring, index: hit.index };
          nodeDrag = { id: event.pointerId, ref, base: state.curves.pieces, draft: null };
          selectNode(ref);
          event.preventDefault();
          return;
        }
      }
      svg.setPointerCapture(event.pointerId);
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      tap = pointers.size === 1 ? { id: event.pointerId, x: event.clientX, y: event.clientY, at: performance.now() } : null;
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = Math.hypot(a.x - b.x, a.y - b.y);
      } else {
        pinch = 0;
      }
    });
    svg.addEventListener('pointermove', (event) => {
      if (nodeDrag && nodeDrag.id === event.pointerId) {
        const point = planMm(event);
        nodeDrag.draft = moveNode(nodeDrag.base, nodeDrag.ref, point.x, point.y);
        const moved = nodeDrag.draft[nodeDrag.ref.piece];
        svg.querySelector(`[data-piece-path="${nodeDrag.ref.piece}"]`)?.setAttribute('d', pathOfPiece(moved));
        const at = listNodeAt(nodeDrag.draft, nodeDrag.ref);
        const mark = svg.querySelector(`[data-node="${nodeKey(nodeDrag.ref)}"]`);
        if (mark && at) { mark.setAttribute('cx', String(at[0])); mark.setAttribute('cy', String(at[1])); }
        return;
      }
      if (cutDrag && cutDrag.id === event.pointerId) {
        const at = planPoint(event);
        const wanted = cutDrag.axis === 'x' ? at.x : model.heightMm - at.y;
        cutDrag.at = clamp(wanted, cutDrag.lo, cutDrag.hi);
        for (const mark of svg.querySelectorAll(`[data-cut-line="${cutDrag.piece}-${cutDrag.axis}-${cutDrag.index}"]`)) {
          if (mark.tagName === 'line') {
            mark.classList.add('is-dragging');
            mark.setAttribute(cutDrag.axis === 'x' ? 'x1' : 'y1', String(cutDrag.at));
            mark.setAttribute(cutDrag.axis === 'x' ? 'x2' : 'y2', String(cutDrag.at));
          } else {
            mark.setAttribute(cutDrag.axis === 'x' ? 'cx' : 'cy', String(cutDrag.at));
          }
        }
        toast(describeCut(cutDrag.piece, cutDrag.axis, cutDrag.index, cutDrag.at));
        return;
      }
      const last = pointers.get(event.pointerId);
      if (!last) return;
      if (tap && Math.hypot(event.clientX - tap.x, event.clientY - tap.y) > 6) tap = null;
      const now = { x: event.clientX, y: event.clientY };
      pointers.set(event.pointerId, now);
      const ctm = svg.getScreenCTM();
      if (!ctm || !plan.box) return;
      if (pointers.size === 1) {
        plan.box = { ...plan.box, x: plan.box.x - ((now.x - last.x) / ctm.a), y: plan.box.y - ((now.y - last.y) / ctm.d) };
        plan.zoomed = true;
        applyPlanBox();
      } else if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch > 0 && distance > 0) {
          zoomPlan(distance / pinch, planPoint({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 }));
        }
        pinch = distance;
      }
    });
    const release = (event) => {
      if (nodeDrag && nodeDrag.id === event.pointerId) {
        const drag = nodeDrag;
        nodeDrag = null;
        if (drag.draft && event.type === 'pointerup') commitNodes(drag.draft, drag.ref);
        else if (drag.draft) renderPlan();
        return;
      }
      if (cutDrag && cutDrag.id === event.pointerId) {
        const drag = cutDrag;
        cutDrag = null;
        if (drag.at !== null && event.type === 'pointerup') setCut(drag.piece, drag.axis, drag.index, drag.at);
        else renderPlan();
        return;
      }
      if (tap && tap.id === event.pointerId && event.type === 'pointerup' && ui.holeMode && performance.now() - tap.at < 800) tapPlan(event);
      tap = null;
      pointers.delete(event.pointerId);
      pinch = 0;
    };
    svg.addEventListener('pointerup', release);
    svg.addEventListener('pointercancel', release);
    svg.addEventListener('dblclick', (event) => {
      if (inNodeMode() && model && !ui.holeMode) { // a double tap on an edge adds a node
        addNodeAt(planMm(event));
        return;
      }
      if (!ui.holeMode) resetPlan();
    });
    svg.setAttribute('tabindex', '0');
    svg.addEventListener('keydown', (event) => {
      if (inNodeMode()) {
        const key = event.key.toLowerCase();
        if ((event.ctrlKey || event.metaKey) && key === 'z') { stepHistory(event.shiftKey ? 'redo' : 'undo'); event.preventDefault(); return; }
        if ((event.ctrlKey || event.metaKey) && key === 'y') { stepHistory('redo'); event.preventDefault(); return; }
        if (nodeEditor.selected) {
          const move = event.shiftKey ? 5 : 0.5;
          if (key === 'delete' || key === 'backspace') { deleteSelected(); event.preventDefault(); return; }
          if (key === 'arrowleft') { nudgeSelected(-move, 0); event.preventDefault(); return; }
          if (key === 'arrowright') { nudgeSelected(move, 0); event.preventDefault(); return; }
          if (key === 'arrowup') { nudgeSelected(0, move); event.preventDefault(); return; }
          if (key === 'arrowdown') { nudgeSelected(0, -move); event.preventDefault(); return; }
          if (key === 'escape') { selectNode(null); event.preventDefault(); return; }
        }
      }
      const step = plan.box ? plan.box.w * 0.1 : 0;
      if (event.key === '+' || event.key === '=') zoomPlan(1.25);
      else if (event.key === '-') zoomPlan(0.8);
      else if (event.key === '0') resetPlan();
      else if (plan.box && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        plan.box = { ...plan.box, x: plan.box.x + (event.key === 'ArrowLeft' ? -step : event.key === 'ArrowRight' ? step : 0), y: plan.box.y + (event.key === 'ArrowUp' ? -step : event.key === 'ArrowDown' ? step : 0) };
        plan.zoomed = true;
        applyPlanBox();
      } else return;
      event.preventDefault();
    });
  }

  // ---- workspace: fold the panels, full screen ----------------------------------------------------------------
  function setFullscreen(on) {
    ui.fullscreen = on;
    host.classList.toggle('is-fullscreen', on);
    document.body.classList.toggle('wr-l3d-fs', on);
    els.fullscreen.setAttribute('aria-pressed', String(on));
    const label = on ? 'Salir de pantalla completa' : 'Pantalla completa';
    els.fullscreen.title = label;
    els.fullscreen.setAttribute('aria-label', label);
    if (on && host.requestFullscreen && !document.fullscreenElement) host.requestFullscreen().catch(() => { /* the fixed overlay is enough */ });
    if (!on && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    viewer?.refresh();
  }

  function applyLayout(layout) {
    host.classList.toggle('hide-panel', !layout.panel);
    host.classList.toggle('hide-results', !layout.results);
    els.togglePanel.setAttribute('aria-pressed', String(layout.panel));
    els.toggleResults.setAttribute('aria-pressed', String(layout.results));
    try { localStorage.setItem('wr-l3d:layout', JSON.stringify(layout)); } catch { /* storage may be blocked */ }
    viewer?.refresh();
  }

  function bindWorkspace() {
    let layout = { panel: true, results: true };
    try { layout = { ...layout, ...JSON.parse(localStorage.getItem('wr-l3d:layout') || '{}') }; } catch { /* keep defaults */ }
    applyLayout(layout);
    els.togglePanel.addEventListener('click', () => { layout = { ...layout, panel: !layout.panel }; applyLayout(layout); });
    els.toggleResults.addEventListener('click', () => { layout = { ...layout, results: !layout.results }; applyLayout(layout); });
    els.fullscreen.addEventListener('click', () => setFullscreen(!ui.fullscreen));
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement && ui.fullscreen) setFullscreen(false); // the browser's own Esc
    });
    document.addEventListener('keydown', (event) => {
      // In real fullscreen the browser keeps Esc for itself (fullscreenchange covers that); here it exits the fixed overlay.
      if (event.key === 'Escape' && ui.fullscreen) setFullscreen(false);
    });
  }

  function bindStage() {
    for (const tab of els.tabs) tab.addEventListener('click', () => setView(tab.dataset.l3dTab));
    els.lights.addEventListener('click', () => {
      ui.lit = !ui.lit;
      els.lights.setAttribute('aria-pressed', String(ui.lit));
      els.swatches.hidden = !ui.lit;
      viewer?.setLit(ui.lit, { color: ui.ledColor, immediate: reducedMotion });
    });
    for (const button of els.swatchButtons) {
      button.addEventListener('click', () => {
        ui.ledColor = button.dataset.l3dLedColor;
        for (const other of els.swatchButtons) other.setAttribute('aria-pressed', String(other === button));
        viewer?.setLit(ui.lit, { color: ui.ledColor, immediate: true });
      });
    }
    els.explode.addEventListener('input', () => { ui.explode = Number(els.explode.value); viewer?.setExplode(ui.explode); });
    els.resetView.addEventListener('click', () => viewer?.resetView());
    els.snapshot.addEventListener('click', () => {
      if (!viewer || ui.view !== '3d') { toast('La imagen se guarda desde la vista 3D.'); return; }
      const link = document.createElement('a');
      link.href = viewer.snapshot();
      link.download = `letras-3d-${slug(designName())}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  }

  // ---- start ------------------------------------------------------------------------------------------
  /** The 3D view cannot work here: keep the tool usable with the plan view (files are generated all the same). */
  function fallBackToFlat(reason) {
    viewer = null;
    host.classList.add('is-flat');
    for (const tab of els.tabs) if (tab.dataset.l3dTab === '3d') tab.hidden = true;
    ui.notice = 'No pudimos dibujar el 3D en este navegador, así que te mostramos la planta. Los archivos se generan igual. Probá con Chrome o Edge actualizado.';
    problems.push(`3D unavailable: ${reason}`);
    setView('plan');
    if (model) renderResults();
  }

  async function startViewer() {
    try {
      const { createViewer } = await import('./wr-l3d-viewer.js?v=aaf4a0ca9e19');
      viewer = createViewer(els.canvas, { glowCanvas: els.glow });
    } catch (error) {
      console.warn('wr-l3d: 3D view unavailable:', error);
      fallBackToFlat(String(error?.message || error));
    }
  }

  /** After the first model is on screen, make sure something was really drawn (a silent GPU failure would look like an empty page). */
  function verifyThreeD() {
    if (!viewer) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      let coverage = 0;
      try { coverage = viewer ? viewer.modelCoverage() : 1; } catch (error) { problems.push(`coverage check failed: ${error?.message || error}`); }
      if (viewer && coverage < 0.0005) fallBackToFlat(`nothing drawn (coverage ${coverage})`);
    }));
  }

  // ---- support diagnostics ----------------------------------------------------------------------------------
  function buildReport() {
    const report = {
      page: location.pathname,
      build: (import.meta.url.match(/[?&]v=([^&]+)/) || [])[1] ?? null,
      userAgent: navigator.userAgent,
      viewport: { width: innerWidth, height: innerHeight, dpr: devicePixelRatio },
      state: { chars: Array.from(state.text).length, fontId: state.fontId, style: state.styleId, heightMm: state.heightMm },
      model: model ? {
        pieces: model.pieces.length, widthMm: Math.round(model.widthMm), heightMm: Math.round(model.heightMm), depthMm: Math.round(model.depthMm),
        params: model.params, partIds: [...new Set(model.pieces.flatMap((piece) => piece.parts.map((part) => part.id)))],
        neon: model.neon ? { ends: model.neon.ends, lengthMm: Math.round(model.neon.lengthMm) } : null,
      } : null,
      viewer: null,
      problems: problems.slice(-12),
    };
    try { report.viewer = viewer ? viewer.diagnose() : 'flat'; } catch (error) { report.viewer = `diagnose failed: ${error?.message || error}`; }
    return report;
  }

  async function copyDiagnostics() {
    const text = JSON.stringify(buildReport(), null, 2);
    try {
      await navigator.clipboard.writeText(text);
      toast('Diagnóstico copiado. Pegalo en el WhatsApp de War Record.');
    } catch {
      console.info('wr-l3d diagnostics', text);
      toast('No pudimos copiarlo. Lo dejamos en la consola del navegador (F12).');
    }
  }

  host.__l3dReport = buildReport; // lets the browser tests read the same report support would receive
  $('[data-l3d-diag]')?.addEventListener('click', copyDiagnostics);

  /** The language changed: what the page keeps written (numbers, hints, lists, messages) is written again in it. */
  function refreshLanguage() {
    syncForm();
    renderExamples();
    if (model) {
      renderParts();
      renderPlan(); // its measures are numbers too
      renderCutList();
      renderOptimizeStats();
      renderResults();
    }
    if (inNodeMode()) renderNodeStats();
    costs.render();
    library.refresh();
    updateLinks();
  }

  const language = createLanguage(host, {
    onChange: refreshLanguage,
    onError: () => toast('No pudimos cargar ese idioma. Probá de nuevo.'),
  });
  host.__l3dLang = language; // lets the browser tests read the same state a visitor sees

  fillPrinterSelect();
  syncForm();
  bindForm();
  bindStage();
  bindPlanNavigation();
  bindHoles();
  bindOptimize();
  bindNodes();
  if (start === 'costs') { // the page about costs opens with its panel open
    const panel = $('[data-l3d-cost]');
    if (panel) panel.open = true;
  }
  bindWorkspace();
  bindActions();
  costs.bind();
  bindProjects();
  renderExamples();
  library.bind();
  tour.bind();
  trackStickyOffset();
  setView('3d');
  els.swatches.hidden = !ui.lit;
  const viewerReady = startViewer();
  rebuild();
}
